/**
 * ETFBookBrowser Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Comprehensive ETF data browser and analysis platform for the Irish Funds ETF Book.
 * This is a sophisticated financial data application with enterprise-grade features:
 * 
 * CORE FEATURES:
 * - Advanced ETF screening with real-time filtering and search
 * - Interactive data visualization with Chart.js integration
 * - Professional export capabilities (Excel/CSV with compliance disclaimers)
 * - Multi-view presentation (table/grid) with responsive design
 * - Comprehensive sorting and pagination system
 * - ETF Intelligence AI integration via Daire chat interface
 * - Full-screen mode with native browser API integration
 * - Print functionality with formatted output
 * - Dynamic market insights and analytics
 * - Real-time market ticker with key metrics
 * 
 * DATA MANAGEMENT:
 * - Smart filtering system with dependency management
 * - Dynamic filter options based on current selection context
 * - Advanced pagination with auto-sizing capabilities
 * - Real-time statistics and contextual insights
 * - Memory-efficient data processing for large datasets
 * 
 * VISUALIZATION ENGINE:
 * - Interactive pie charts for asset class and provider distribution
 * - Bar charts for geographic AUM analysis
 * - Time series line charts for historical trends
 * - Modal-based chart expansion with enhanced interactivity
 * - Chart.js with data labels plugin for professional presentation
 * - Responsive chart sizing with resize handlers
 * 
 * BUSINESS INTELLIGENCE:
 * - Dynamic insights panel with IFIA-branded analytics
 * - Market leadership and ESG penetration analysis
 * - Cost structure analysis and low-cost fund identification
 * - Geographic market share analysis with Irish focus
 * - Provider dominance and concentration metrics
 * - Real-time calculation of market statistics
 * 
 * TECHNICAL ARCHITECTURE:
 * - React Hooks for state management and lifecycle control
 * - useCallback for performance optimization of event handlers
 * - Ref-based chart management for stability
 * - Event-driven fullscreen API integration
 * - Professional error handling and graceful degradation
 * - Production-ready with comprehensive documentation
 * 
 * @param {Object} props - Component props
 * @param {Array} props.etfData - Array of ETF objects with fund details, performance, and metadata
 * @param {Object} props.graphData - Pre-computed chart data for overview visualizations
 * @param {Object} props.graphData.auMByAssetClassOverview - AUM breakdown by asset class
 * @param {Object} props.graphData.auMByProviderOverview - AUM breakdown by provider
 * @param {boolean} props.loading - Loading state indicator for data fetching
 * @param {boolean} props.mobileMode - Mobile mode flag for touch-friendly UI
 * @returns {JSX.Element} Complete ETF browser with screening, analytics, and AI integration
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoadingSpinner from '../Common/LoadingSpinner';
import ETFDaireChat from '../Daire/ETFDaireChat';
import apiHelper from '../../utils/apiHelper';
import './ETFBookBrowser.css';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Colors,
  Filler
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Colors,
  Filler,
  ChartDataLabels
);

/**
 * ETFBookBrowser Component
 * 
 * Main component providing comprehensive ETF data browsing, filtering, analysis,
 * and visualization capabilities with integrated AI assistance.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.etfData - Complete ETF dataset
 * @param {Object} props.graphData - Pre-computed visualization data
 * @param {Array} props.settings - Application settings including data dates
 * @param {boolean} props.loading - Data loading state
 * @param {boolean} props.mobileMode - Mobile mode flag for touch-friendly UI
 * @returns {JSX.Element} Full-featured ETF browser interface
 */
function ETFBookBrowser({ etfData, graphData, settings, loading, mobileMode = false }) {
  const [activeTab, setActiveTab] = useState('screener');
  const [filteredData, setFilteredData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(mobileMode ? 'grid' : 'table'); // Default to grid for mobile

  // Filter state
  const [selectedAssetClass, setSelectedAssetClass] = useState('all');
  const [selectedDomicile, setSelectedDomicile] = useState('all');
  const [selectedESG, setSelectedESG] = useState('all');
  const [selectedESGCategory, setSelectedESGCategory] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState('all');
  const [selectedSFDR, setSelectedSFDR] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sortField, setSortField] = useState('aum');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal state
  const [selectedETF, setSelectedETF] = useState(null);
  const [chartModal, setChartModal] = useState({ isOpen: false, chartType: null, title: '', data: null, options: null });
  const [exportModal, setExportModal] = useState(false);
  const [daireModal, setDaireModal] = useState(false);
  
  // Chart configuration state
  const [flowChartFilter, setFlowChartFilter] = useState('ireland');
  const [selectedStartYear, setSelectedStartYear] = useState('2025');
  const [cumulativeViewMode, setCumulativeViewMode] = useState('from_baseline'); // 'from_baseline' or 'since_inception'
  const [flowsViewMode, setFlowsViewMode] = useState('CUMULATIVE_FLOWS');
  const [monthlyFlowsChartType, setMonthlyFlowsChartType] = useState('bar');
  
  // Re-render flow charts when filter changes
  useEffect(() => {
    if (activeTab === 'analytics' && graphData) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        if (monthlyFlowsBarRef.current) {
          monthlyFlowsBarRef.current.update('resize');
        }
        if (cumulativeFlowsLineRef.current) {
          cumulativeFlowsLineRef.current.update('resize');
        }

      }, 100);
    }
  }, [flowChartFilter, selectedStartYear, activeTab, graphData, flowsViewMode, monthlyFlowsChartType, cumulativeViewMode]);

  // Tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Chart refs to prevent shrinking 
  const pieChart1Ref = useRef(null);
  const barChartRef = useRef(null);
  const pieChart2Ref = useRef(null);
  const pieChart3Ref = useRef(null);

  // Additional chart refs for time series charts
  const aumAssetClassPieRef = useRef(null);
  const aumAssetClassIrelandPieRef = useRef(null);
  const aumProviderPieRef = useRef(null);
  const aumAssetClassLineRef = useRef(null);
  // FLOW CHART REFS - Real flow data calculated from AUM changes! 🚀
  const monthlyFlowsBarRef = useRef(null);
  const cumulativeFlowsLineRef = useRef(null);

  //  FULLSCREEN! 
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Modal positioning ref
  const modalRef = useRef(null);

  // Chart resize handler to prevent shrinking
  useEffect(() => {
    const handleChartResize = () => {
      // Existing charts
      if (pieChart1Ref.current) pieChart1Ref.current.resize();
      if (barChartRef.current) barChartRef.current.resize();
      if (pieChart2Ref.current) pieChart2Ref.current.resize();
      if (pieChart3Ref.current) pieChart3Ref.current.resize();

      // New time series charts
      if (aumAssetClassPieRef.current) aumAssetClassPieRef.current.resize();
      if (aumAssetClassIrelandPieRef.current) aumAssetClassIrelandPieRef.current.resize();
      if (aumProviderPieRef.current) aumProviderPieRef.current.resize();
      if (aumAssetClassLineRef.current) aumAssetClassLineRef.current.resize();
      // Flow chart resizing logic
      if (monthlyFlowsBarRef.current) monthlyFlowsBarRef.current.resize();
      if (cumulativeFlowsLineRef.current) cumulativeFlowsLineRef.current.resize();
    };

    // Initial render stability - prevents shrinking on first load
    const initialRenderTimer = setTimeout(() => {
      handleChartResize();
    }, 200);

    // Debounced resize handler
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleChartResize, 100);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
      clearTimeout(initialRenderTimer);
    };
  }, []);

  // Chart stability when tab changes
  useEffect(() => {
    if (activeTab === 'overview') {
      const tabChangeTimer = setTimeout(() => {
        // Existing charts
        if (pieChart1Ref.current) pieChart1Ref.current.resize();
        if (barChartRef.current) barChartRef.current.resize();
        if (pieChart2Ref.current) pieChart2Ref.current.resize();
        if (pieChart3Ref.current) pieChart3Ref.current.resize();

        // New time series charts
        if (aumAssetClassPieRef.current) aumAssetClassPieRef.current.resize();
        if (aumAssetClassIrelandPieRef.current) aumAssetClassIrelandPieRef.current.resize();
        if (aumProviderPieRef.current) aumProviderPieRef.current.resize();
        if (aumAssetClassLineRef.current) aumAssetClassLineRef.current.resize();
        // Flow chart tab change resizing logic
        if (monthlyFlowsBarRef.current) monthlyFlowsBarRef.current.resize();
        if (cumulativeFlowsLineRef.current) cumulativeFlowsLineRef.current.resize();
      }, 150);

      return () => clearTimeout(tabChangeTimer);
    }
  }, [activeTab]);

  // Handle URL parameter to auto-open ETF chat
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openETFChat') === 'true') {
      setDaireModal(true);
      if (apiHelper._cachedUserEmail) {
        apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'ETFDaireModal', 'ETF Daire AI', 'DaireAI');
      }
      // Clean up the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Filter, sort, and paginate data - consolidated to prevent race conditions
  useEffect(() => {
    let filtered = etfData || [];

    // Text search
    if (searchTerm) {
      filtered = filtered.filter(etf =>
        etf.fundName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        etf.fundISIN.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by Asset Class
    if (selectedAssetClass !== 'all') {
      filtered = filtered.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }

    // Filter by Domicile
    if (selectedDomicile !== 'all') {
      filtered = filtered.filter(etf => etf.fundDomicile === selectedDomicile);
    }

    // Filter by ESG
    if (selectedESG !== 'all') {
      filtered = filtered.filter(etf => etf.fundESG === selectedESG);
    }

    // Filter by ESG Category
    if (selectedESGCategory !== 'all') {
      filtered = filtered.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }

    // Filter by Currency
    if (selectedCurrency !== 'all') {
      filtered = filtered.filter(etf => etf.fundCurrency === selectedCurrency);
    }

    // Filter by SFDR Article
    if (selectedSFDR !== 'all') {
      filtered = filtered.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle different data types
      if (sortField === 'fundTER' || sortField === 'aum') {
        // Numeric sorting
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else {
        // String sorting (case insensitive)
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    // Calculate pagination values
    const newTotalPages = Math.ceil(sorted.length / pageSize);
    let pageToUse = currentPage;

    // Reset to page 1 if current page is out of bounds (due to filtering/sorting)
    if (pageToUse > newTotalPages || pageToUse < 1) {
      pageToUse = 1;
    }

    // Update all state atomically
    setFilteredData(sorted);
    setTotalItems(sorted.length);
    setTotalPages(newTotalPages);

    // Update current page if it was out of bounds
    if (pageToUse !== currentPage) {
      setCurrentPage(pageToUse);
    }

    // Paginate immediately with the correct page
    const startIndex = (pageToUse - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = sorted.slice(startIndex, endIndex);
    setDisplayData(paginated);

  }, [searchTerm, selectedAssetClass, selectedDomicile, selectedESG, selectedESGCategory, selectedCurrency, selectedSFDR, etfData, pageSize, sortField, sortDirection, currentPage]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      // Scroll to top of content
      document.querySelector('.etf-filters-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 with new page size
  };

  // Auto-adjust page size when it's inappropriate for current results
  React.useEffect(() => {
    if (totalItems > 0) {
      // Adjust UP when pageSize is too small for the current result set
      if (pageSize < totalItems && pageSize < 12) {
        // For small result sets (≤12), show all items
        // For larger result sets, default to 12
        const newPageSize = totalItems <= 12 ? totalItems : 12;
        setPageSize(newPageSize);
        setCurrentPage(1);
      }
      // Adjust DOWN when pageSize is larger than available items
      else if (pageSize > totalItems) {
        // For small result sets (≤12), always adjust down to show all items
        // For larger result sets, only adjust if pageSize is much larger (3x+)
        if (totalItems <= 12 || pageSize > totalItems * 3) {
          const appropriateSize = totalItems <= 12 ? totalItems : Math.min(12, totalItems);
          setPageSize(appropriateSize);
          setCurrentPage(1);
        }
      }
    }
  }, [totalItems, pageSize]);

  /**
   * Generate dynamic filter options based on current selections
   * 
   * Implements smart filtering logic similar to MediaCentre component, where
   * each filter dropdown only shows options that are available given the
   * current state of other filters. This prevents users from selecting
   * filter combinations that would result in zero results.
   * 
   * @returns {Object} Object containing arrays of available filter options
   * @returns {Array} returns.assetClasses - Available asset class options
   * @returns {Array} returns.domiciles - Available domicile options  
   * @returns {Array} returns.currencies - Available currency options
   * @returns {Array} returns.esgOptions - Available ESG options
   * @returns {Array} returns.esgCategories - Available ESG category options
   * @returns {Array} returns.sfdrArticles - Available SFDR article options
   */
  const getDynamicFilterOptions = () => {
    const data = etfData || [];

    // For Asset Class: filter by everything EXCEPT asset class selection
    let assetClassData = [...data];
    if (selectedDomicile !== 'all') {
      assetClassData = assetClassData.filter(etf => etf.fundDomicile === selectedDomicile);
    }
    if (selectedCurrency !== 'all') {
      assetClassData = assetClassData.filter(etf => etf.fundCurrency === selectedCurrency);
    }
    if (selectedESG !== 'all') {
      assetClassData = assetClassData.filter(etf => etf.fundESG === selectedESG);
    }
    if (selectedESGCategory !== 'all') {
      assetClassData = assetClassData.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }
    if (selectedSFDR !== 'all') {
      assetClassData = assetClassData.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // For Domicile: filter by everything EXCEPT domicile selection
    let domicileData = [...data];
    if (selectedAssetClass !== 'all') {
      domicileData = domicileData.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }
    if (selectedCurrency !== 'all') {
      domicileData = domicileData.filter(etf => etf.fundCurrency === selectedCurrency);
    }
    if (selectedESG !== 'all') {
      domicileData = domicileData.filter(etf => etf.fundESG === selectedESG);
    }
    if (selectedESGCategory !== 'all') {
      domicileData = domicileData.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }
    if (selectedSFDR !== 'all') {
      domicileData = domicileData.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // For Currency: filter by everything EXCEPT currency selection
    let currencyData = [...data];
    if (selectedAssetClass !== 'all') {
      currencyData = currencyData.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }
    if (selectedDomicile !== 'all') {
      currencyData = currencyData.filter(etf => etf.fundDomicile === selectedDomicile);
    }
    if (selectedESG !== 'all') {
      currencyData = currencyData.filter(etf => etf.fundESG === selectedESG);
    }
    if (selectedESGCategory !== 'all') {
      currencyData = currencyData.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }
    if (selectedSFDR !== 'all') {
      currencyData = currencyData.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // For ESG: filter by everything EXCEPT ESG selection
    let esgData = [...data];
    if (selectedAssetClass !== 'all') {
      esgData = esgData.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }
    if (selectedDomicile !== 'all') {
      esgData = esgData.filter(etf => etf.fundDomicile === selectedDomicile);
    }
    if (selectedCurrency !== 'all') {
      esgData = esgData.filter(etf => etf.fundCurrency === selectedCurrency);
    }
    if (selectedESGCategory !== 'all') {
      esgData = esgData.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }
    if (selectedSFDR !== 'all') {
      esgData = esgData.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // For ESG Category: filter by everything EXCEPT ESG Category selection
    let esgCategoryData = [...data];
    if (selectedAssetClass !== 'all') {
      esgCategoryData = esgCategoryData.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }
    if (selectedDomicile !== 'all') {
      esgCategoryData = esgCategoryData.filter(etf => etf.fundDomicile === selectedDomicile);
    }
    if (selectedCurrency !== 'all') {
      esgCategoryData = esgCategoryData.filter(etf => etf.fundCurrency === selectedCurrency);
    }
    if (selectedESG !== 'all') {
      esgCategoryData = esgCategoryData.filter(etf => etf.fundESG === selectedESG);
    }
    if (selectedSFDR !== 'all') {
      esgCategoryData = esgCategoryData.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // For SFDR: filter by everything EXCEPT SFDR selection
    let sfdrData = [...data];
    if (selectedAssetClass !== 'all') {
      sfdrData = sfdrData.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }
    if (selectedDomicile !== 'all') {
      sfdrData = sfdrData.filter(etf => etf.fundDomicile === selectedDomicile);
    }
    if (selectedCurrency !== 'all') {
      sfdrData = sfdrData.filter(etf => etf.fundCurrency === selectedCurrency);
    }
    if (selectedESG !== 'all') {
      sfdrData = sfdrData.filter(etf => etf.fundESG === selectedESG);
    }
    if (selectedESGCategory !== 'all') {
      sfdrData = sfdrData.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }

    // Extract unique values for each filter
    const dynamicOptions = {
      assetClasses: [...new Set(assetClassData.map(etf => etf.fundAssetClass))].filter(Boolean).sort(),
      domiciles: [...new Set(domicileData.map(etf => etf.fundDomicile))].filter(Boolean).sort(),
      currencies: [...new Set(currencyData.map(etf => etf.fundCurrency))].filter(Boolean).sort(),
      esgOptions: [...new Set(esgData.map(etf => etf.fundESG))].filter(Boolean).sort(),
      esgCategories: [...new Set(esgCategoryData.map(etf => etf.fundESGCategory))].filter(Boolean).sort(),
      sfdrArticles: [...new Set(sfdrData.map(etf => etf.fundSFDR))].filter(Boolean).sort()
    };

    // Always include currently selected options (so they don't disappear from dropdowns)
    if (selectedAssetClass !== 'all' && !dynamicOptions.assetClasses.includes(selectedAssetClass)) {
      dynamicOptions.assetClasses.push(selectedAssetClass);
      dynamicOptions.assetClasses.sort();
    }
    if (selectedDomicile !== 'all' && !dynamicOptions.domiciles.includes(selectedDomicile)) {
      dynamicOptions.domiciles.push(selectedDomicile);
      dynamicOptions.domiciles.sort();
    }
    if (selectedCurrency !== 'all' && !dynamicOptions.currencies.includes(selectedCurrency)) {
      dynamicOptions.currencies.push(selectedCurrency);
      dynamicOptions.currencies.sort();
    }
    if (selectedESG !== 'all' && !dynamicOptions.esgOptions.includes(selectedESG)) {
      dynamicOptions.esgOptions.push(selectedESG);
      dynamicOptions.esgOptions.sort();
    }
    if (selectedESGCategory !== 'all' && !dynamicOptions.esgCategories.includes(selectedESGCategory)) {
      dynamicOptions.esgCategories.push(selectedESGCategory);
      dynamicOptions.esgCategories.sort();
    }
    if (selectedSFDR !== 'all' && !dynamicOptions.sfdrArticles.includes(selectedSFDR)) {
      dynamicOptions.sfdrArticles.push(selectedSFDR);
      dynamicOptions.sfdrArticles.sort();
    }

    return dynamicOptions;
  };

  /**
   * Smart filter change handler with automatic dependent filter reset
   * 
   * Intelligently manages filter interdependencies by automatically resetting
   * filters that would have no valid options after a filter change. This ensures
   * the UI remains consistent and prevents impossible filter combinations.
   * 
   * @param {string} filterType - Type of filter being changed (assetClass, domicile, etc.)
   * @param {string} value - New value for the filter ('all' or specific option)
   */
  const handleSmartFilterChange = (filterType, value) => {
    const data = etfData || [];
    let filteredData = [...data];

    // Apply the new filter value
    if (filterType === 'assetClass' && value !== 'all') {
      filteredData = filteredData.filter(etf => etf.fundAssetClass === value);
    } else if (selectedAssetClass !== 'all' && filterType !== 'assetClass') {
      filteredData = filteredData.filter(etf => etf.fundAssetClass === selectedAssetClass);
    }

    if (filterType === 'domicile' && value !== 'all') {
      filteredData = filteredData.filter(etf => etf.fundDomicile === value);
    } else if (selectedDomicile !== 'all' && filterType !== 'domicile') {
      filteredData = filteredData.filter(etf => etf.fundDomicile === selectedDomicile);
    }

    if (filterType === 'currency' && value !== 'all') {
      filteredData = filteredData.filter(etf => etf.fundCurrency === value);
    } else if (selectedCurrency !== 'all' && filterType !== 'currency') {
      filteredData = filteredData.filter(etf => etf.fundCurrency === selectedCurrency);
    }

    if (filterType === 'esg' && value !== 'all') {
      filteredData = filteredData.filter(etf => etf.fundESG === value);
    } else if (selectedESG !== 'all' && filterType !== 'esg') {
      filteredData = filteredData.filter(etf => etf.fundESG === selectedESG);
    }

    if (filterType === 'esgCategory' && value !== 'all') {
      filteredData = filteredData.filter(etf => etf.fundESGCategory === value);
    } else if (selectedESGCategory !== 'all' && filterType !== 'esgCategory') {
      filteredData = filteredData.filter(etf => etf.fundESGCategory === selectedESGCategory);
    }

    if (filterType === 'sfdr' && value !== 'all') {
      filteredData = filteredData.filter(etf => etf.fundSFDR === value);
    } else if (selectedSFDR !== 'all' && filterType !== 'sfdr') {
      filteredData = filteredData.filter(etf => etf.fundSFDR === selectedSFDR);
    }

    // Get available options after this filter change
    const availableOptions = {
      assetClasses: [...new Set(filteredData.map(etf => etf.fundAssetClass))].filter(Boolean),
      domiciles: [...new Set(filteredData.map(etf => etf.fundDomicile))].filter(Boolean),
      currencies: [...new Set(filteredData.map(etf => etf.fundCurrency))].filter(Boolean),
      esgOptions: [...new Set(filteredData.map(etf => etf.fundESG))].filter(Boolean),
      esgCategories: [...new Set(filteredData.map(etf => etf.fundESGCategory))].filter(Boolean),
      sfdrArticles: [...new Set(filteredData.map(etf => etf.fundSFDR))].filter(Boolean)
    };

    // Apply the main filter change
    if (filterType === 'assetClass') {
      setSelectedAssetClass(value);
    } else if (filterType === 'domicile') {
      setSelectedDomicile(value);
    } else if (filterType === 'currency') {
      setSelectedCurrency(value);
    } else if (filterType === 'esg') {
      setSelectedESG(value);
    } else if (filterType === 'esgCategory') {
      setSelectedESGCategory(value);
    } else if (filterType === 'sfdr') {
      setSelectedSFDR(value);
    }

    // Reset dependent filters if their current value is no longer available
    if (filterType !== 'assetClass' && selectedAssetClass !== 'all' && !availableOptions.assetClasses.includes(selectedAssetClass)) {
      setSelectedAssetClass('all');
    }

    if (filterType !== 'domicile' && selectedDomicile !== 'all' && !availableOptions.domiciles.includes(selectedDomicile)) {
      setSelectedDomicile('all');
    }

    if (filterType !== 'currency' && selectedCurrency !== 'all' && !availableOptions.currencies.includes(selectedCurrency)) {
      setSelectedCurrency('all');
    }

    if (filterType !== 'esg' && selectedESG !== 'all' && !availableOptions.esgOptions.includes(selectedESG)) {
      setSelectedESG('all');
    }

    if (filterType !== 'esgCategory' && selectedESGCategory !== 'all' && !availableOptions.esgCategories.includes(selectedESGCategory)) {
      setSelectedESGCategory('all');
    }

    if (filterType !== 'sfdr' && selectedSFDR !== 'all' && !availableOptions.sfdrArticles.includes(selectedSFDR)) {
      setSelectedSFDR('all');
    }
  };

  const filterOptions = getDynamicFilterOptions();

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || selectedAssetClass !== 'all' ||
    selectedDomicile !== 'all' || selectedESG !== 'all' ||
    selectedESGCategory !== 'all' || selectedCurrency !== 'all' || selectedSFDR !== 'all';

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedAssetClass('all');
    setSelectedDomicile('all');
    setSelectedESG('all');
    setSelectedESGCategory('all');
    setSelectedCurrency('all');
    setSelectedSFDR('all');
  };

  // Action handlers for new buttons
  const handleExportToExcel = () => {
    // Show confirmation modal with disclaimer
    setExportModal(true);
    if (apiHelper._cachedUserEmail) {
      apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'ETFExportModal', 'Export to Excel', 'Export');
    }
  };

  const confirmExportToExcel = () => {
    // Close modal and proceed with export
    setExportModal(false);

    // Export current view (filtered data) to Excel

    // Prepare headers for Excel
    const headers = [
      'Fund Name',
      'ISIN',
      'Domicile',
      'Currency',
      'Asset Class',
      'Geographic Focus',
      'SFDR Article',
      'TER (%)',
      'AUM (EUR M)',
      'AUM As Of Date'
    ];

    // Helper function to format AUM for Excel (prevent scientific notation)
    const formatAUMForExcel = (aum) => {
      if (!aum) return '';
      const aumValue = parseFloat(aum.toString().replace(/[€,$M\s]/g, ''));
      if (isNaN(aumValue)) return aum;

      // Convert to millions and format as text with quotes to prevent scientific notation
      const aumInMillions = (aumValue / 1000000).toFixed(2);
      return aumInMillions;
    };

    // Prepare rows with current page view only
    const rows = displayData.map(etf => [

      etf.fundName || '',
      etf.fundISIN || '',
      etf.fundDomicile || '',
      etf.fundCurrency || '',
      etf.fundAssetClass || '',
      etf.fundAssetClassGeoFocus || '',
      formatSFDR(etf.fundSFDR) || '',
      etf.fundTER || '',
      formatAUMForExcel(etf.aum),
      etf.aumAsOfDate || ''
    ]);

    // Add summary information at the top
    const currentDate = new Date().toLocaleDateString();
    const appliedFilters = getAppliedFilters();
    const filterSummary = appliedFilters.length > 0
      ? appliedFilters.map(f => `${f.label}: ${f.value}`).join(', ')
      : 'No filters applied';

    const summary = [
      ['ETF Screener Export - Current View'],
      ['Data provided for member insight only. Redistribution prohibited without consent.'],
      [], // Empty row for spacing
      ['Export Date', currentDate],
      ['Current Page', `${currentPage} of ${totalPages}`],
      ['Items in This View', displayData.length],
      ['Total Filtered Results', filteredData.length],
      ['Applied Filters', filterSummary],
      ['Current Sort', `${sortField} (${sortDirection})`],
      [], // Empty row for spacing
      headers,
      ...rows
    ];

    // Convert to CSV
    const csvContent = summary.map(row =>
      row.map(cell => {
        // Convert cell to string and handle special characters
        const cellStr = String(cell || '');
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    ).join('\n');

    // Create and trigger download with proper encoding
    const BOM = '\uFEFF'; // Add BOM for proper UTF-8 encoding in Excel
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    // Generate filename with current date and page info
    const pageText = totalPages > 1 ? `_page${currentPage}` : '';
    const fileName = `etf_book_view_${new Date().toISOString().split('T')[0]}${pageText}.csv`;
    link.setAttribute('download', fileName);

    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Excel export completed successfully
  };

  const cancelExportToExcel = () => {
    setExportModal(false);
  };

  const handlePrint = () => {
          // Add visible marker for print dialog
    document.body.style.border = '5px solid red';

    window.print();

    // Remove marker after print dialog
    setTimeout(() => {
      document.body.style.border = '';
    }, 1000);
  };

  const handleCallDaire = () => {
    // Activate Daire AI with ETF data context
    setDaireModal(true);
    if (apiHelper._cachedUserEmail) {
      apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'ETFDaireModal', 'ETF Daire AI', 'DaireAI');
    }
  };

  const closeDaireModal = () => {
    setDaireModal(false);
  };

  // Real ETF data print function
  const handleRealPrint = () => {
    try {
      // Create a separate window for printing
      const printWindow = window.open('', '_blank', 'width=1200,height=800');

      // Generate table rows from real data
      const tableRows = displayData.map(etf => `
        <tr>

          <td>${etf.fundName || ''}</td>
          <td>${etf.fundDomicile || ''}</td>
          <td>${etf.fundCurrency || ''}</td>
          <td>${etf.fundAssetClass || ''}</td>
          <td>${etf.fundAssetClassGeoFocus || ''}</td>
          <td>${etf.fundESG || ''}</td>
          <td>${etf.fundESGCategory || ''}</td>
          <td>${formatSFDR(etf.fundSFDR) || ''}</td>
        </tr>
      `).join('');

      // Get current filter info
      const appliedFilters = getAppliedFilters();
      const filterText = appliedFilters.length > 0
        ? appliedFilters.map(f => `${f.label}: ${f.value}`).join(', ')
        : 'No filters applied';

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>ETF Screener - Current View</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 15px; font-size: 11px; }
            h1 { color: #2F7D32; font-size: 18px; margin-bottom: 10px; }
            .info { margin-bottom: 15px; font-size: 10px; color: #666; }
            table { border-collapse: collapse; width: 100%; font-size: 9px; }
            th, td { border: 1px solid #ccc; padding: 4px 3px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; text-align: center; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            @page { margin: 0.5in; size: landscape; }
          </style>
        </head>
        <body>
          <h1>ETF Screener - Current View</h1>
          <div class="info">
            <strong>Print Date:</strong> ${new Date().toLocaleDateString()} | 
            <strong>Page:</strong> ${currentPage} of ${totalPages} | 
            <strong>Items Shown:</strong> ${displayData.length} | 
            <strong>Total Filtered:</strong> ${filteredData.length}<br/>
            <strong>Applied Filters:</strong> ${filterText}<br/>
            <strong>Sorted by:</strong> ${sortField} (${sortDirection === 'asc' ? 'ascending' : 'descending'})
          </div>
          
          <table>
            <thead>
              <tr>
    
                <th>Fund Name</th>
                <th>Domicile</th>
                <th>Currency</th>
                <th>Asset Class</th>
                <th>Geographic Focus</th>
                <th>ESG</th>
                <th>ESG Category</th>
                <th>SFDR</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);

      // Print window opened successfully
    } catch (error) {
      // Print operation failed - log error but don't disrupt user experience
      console.error('Print operation failed:', error.message);
    }
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as sort field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to page 1 when sorting changes
    setCurrentPage(1);
  };

  // Get sort icon for table headers
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="fas fa-sort sort-icon inactive"></i>;
    }
    return sortDirection === 'asc'
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  // Modal handlers
  const handleRowClick = (etf) => {
    setSelectedETF(etf);
    if (apiHelper._cachedUserEmail) {
      apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'ETFDetailModal', etf?.fundName || 'ETF', 'ETF');
    }
  };

  const closeETFModal = () => {
    setSelectedETF(null);
  };

  /**
   * Format SFDR (Sustainable Finance Disclosure Regulation) values for display
   * 
   * Converts verbose SFDR classifications into compact, readable format.
   * Handles various input formats and edge cases.
   * 
   * @param {string|number} sfdrValue - Raw SFDR classification value
   * @returns {string} Formatted SFDR display value (e.g., "Art 8", "N/A")
   */
  const formatSFDR = (sfdrValue) => {
    if (!sfdrValue) return '-';
    const sfdrStr = sfdrValue.toString().toUpperCase();

    // Convert long SFDR text to compact readable format
    if (sfdrStr.includes('NOT APPLICABLE')) return 'N/A';
    if (sfdrStr.includes('ARTICLE 6')) return 'Art 6';
    if (sfdrStr.includes('ARTICLE 8')) return 'Art 8';
    if (sfdrStr.includes('ARTICLE 9')) return 'Art 9';
    if (sfdrStr.includes('ARTICLE')) return sfdrStr.replace('ARTICLE', 'Art');

    return sfdrValue;
  };

  /**
   * Format Total Expense Ratio (TER) values for consistent display
   * 
   * Ensures TER values are displayed in basis points format, handling various 
   * input formats. Converts decimal values (like 0.0020) to basis points (20bps).
   * 
   * @param {string|number} ter - Raw TER value (decimal or percentage)
   * @returns {string} Formatted TER in basis points (e.g., "20bps")
   */
  const formatTER = (ter) => {
    if (!ter || ter === 0) return '-';

    // Remove any existing % symbol and parse as float
    const terFloat = parseFloat(ter.toString().replace('%', ''));
    if (isNaN(terFloat)) return ter;

    // Convert to basis points
    // If value is less than 1, assume it's in decimal form (0.0020 = 20bps)
    // If value is 1 or greater, assume it's already in percentage form (0.20 = 20bps)
    const basisPoints = terFloat < 1 ? terFloat * 10000 : terFloat * 100;

    // Format as whole number for basis points
    return `${Math.round(basisPoints)}bps`;
  };

  /**
   * Format Assets Under Management (AUM) values with intelligent scaling
   * 
   * Converts AUM values to appropriate scale (millions/billions) with
   * Euro currency formatting. Handles various input formats and edge cases
   * for consistent display across different scales.
   * 
   * @param {string|number} aum - Raw AUM value (may include currency symbols)
   * @returns {string} Formatted AUM with appropriate scale (e.g., "€1.2B", "€150M")
   */
  const formatAUM = (aum) => {
    if (!aum) return '-';
    const aumValue = parseFloat(aum.toString().replace(/[€,$MB\s]/g, ''));
    if (isNaN(aumValue)) return aum;

    // If the value looks like it's already in millions (8+ digits), treat it as raw value
    if (aumValue >= 1000000) {
      const billions = aumValue / 1000000000;
      const millions = aumValue / 1000000;

      if (billions >= 1) {
        return `€${billions.toFixed(2)}B`;
      } else {
        return `€${millions.toFixed(0)}M`;
      }
    } else if (aumValue >= 1000) {
      return `€${(aumValue / 1000).toFixed(1)}B`;
    } else if (aumValue >= 1) {
      return `€${aumValue.toFixed(0)}M`;
    } else {
      return `€${aumValue.toFixed(1)}M`;
    }
  };

  // Helper function to parse dates consistently (DD/MM/YYYY or YYYY-MM-DD)
  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    
    // Handle ISO format (YYYY-MM-DD)
    if (dateString.includes('-') && dateString.split('-')[0].length === 4) {
      return new Date(dateString);
    }
    
    // Handle DD/MM/YYYY format (Irish format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        // Assume DD/MM/YYYY format
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // Month is 0-indexed
        const year = parseInt(parts[2]);
        return new Date(year, month, day);
      }
    }
    
    // Fallback to direct parsing
    return new Date(dateString);
  };

  // Format date to Irish format (DD/MM/YYYY)
  const formatDateIrish = (dateString) => {
    if (!dateString) return '-';

    // Use the consistent parseDate function
    const date = parseDate(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if we can't parse it
    }

    // Format to DD/MM/YYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Generate overview stats
  const getDynamicPageSizes = () => {
    if (totalItems === 0) {
      return [12]; // Default when no results
    }

    // For small result sets, show appropriate smaller options
    if (totalItems <= 12) {
      const smallSizes = [];
      if (totalItems >= 6) smallSizes.push(6);
      smallSizes.push(totalItems); // Always include "show all" for small sets
      return [...new Set(smallSizes)].sort((a, b) => a - b);
    }

    const standardSizes = [12, 24, 30, 50, 75, 100];
    const relevantSizes = [];

    // Include 12 as baseline for larger sets
    relevantSizes.push(12);

    // Include other standard sizes that make sense
    standardSizes.forEach(size => {
      if (size > 12 && size <= totalItems) {
        relevantSizes.push(size);
      }
    });

    // Include "show all" option for reasonable counts
    if (totalItems <= 200 && totalItems > 12 && !relevantSizes.includes(totalItems)) {
      relevantSizes.push(totalItems);
    }

    // Remove duplicates and sort
    return [...new Set(relevantSizes)].sort((a, b) => a - b);
  };

  /**
   * Generate comprehensive overview statistics with business intelligence insights
   * 
   * Calculates key market metrics including AUM totals, market concentration,
   * ESG penetration, geographic distribution, cost analysis, and provider
   * market share. This function powers the insights panel and overview dashboard.
   * 
   * @param {Array} data - Array of ETF objects to analyze
   * @returns {Object} Comprehensive statistics object with business intelligence metrics
   * @returns {number} returns.totalETFs - Total number of ETFs
   * @returns {number} returns.totalAUM - Total assets under management
   * @returns {number} returns.avgTER - AUM-weighted average total expense ratio
   * @returns {Object} returns.largestETF - ETF with highest AUM
   * @returns {Array} returns.topProvider - [providerName, {count, aum}] for market leader
   * @returns {number} returns.esgPenetration - Percentage of AUM in ESG funds
   * @returns {number} returns.irishMarketShare - Irish domicile market share percentage
   * @returns {number} returns.lowCostPenetration - Percentage of low-cost funds (≤0.5% TER)
   */
  const getOverviewStats = (data) => {
    const totalAUM = data.reduce((sum, etf) => sum + (etf.aum || 0), 0);



    // STATS FROM REAL DATA
    const largestETF = data.reduce((max, etf) =>
      (etf.aum || 0) > (max.aum || 0) ? etf : max, data[0] || {});

    // 💪 PROVIDER MARKET ANALYSIS - Extract from fund name with proper filtering
    const providerStats = new Map();
    
    // Known legitimate ETF providers
    const knownProviders = [
      'ISHARES', 'VANGUARD', 'SPDR', 'XTRACKERS', 'AMUNDI', 'LYXOR', 'HSBC', 'BNP', 'INVESCO',
      'FIDELITY', 'GOLDMAN', 'JPMORGAN', 'UBS', 'WISDOMTREE', 'VANECK', 'PROSHARES', 'DIREXION',
      'FIRST', 'SCHWAB', 'FRANKLIN', 'ABRDN', 'AVANTIS', 'ARK', 'AUAG', 'CHIMERA', 'ETC',
      'FINAMEX', 'EMQQ', 'ALERIAN', 'AXA', 'OSSIAM', 'KRANESHARES', 'COLUMBIA', 'CAMBRIA',
      'AMPLIFY', 'ALPS', 'ROUNDHILL', 'DEFIANCE', 'PACER', 'NUVEEN', 'GLOBAL', 'RIZE',
      'HAR', 'PURPOSE', 'EVOLVE', 'HORIZONS', 'HARVEST', 'SOLACTIVE', 'STOXX', 'MSCI'
    ];

    // Junk words that appear in fund names but aren't providers
    const junkWords = [
      'THE', 'A', 'AN', 'AI-ENHANCED', 'ARTIFICIAL', 'GLOBAL', 'INTERNATIONAL', 'WORLD', 'US', 'EUROPEAN', 'AMERICAN',
      'ENHANCED', 'SMART', 'STRATEGIC', 'ACTIVE', 'PASSIVE', 'INDEX', 'FUND', 'ETF', 'TRUST', 'SERIES',
      'ULTRA', 'SUPER', 'MEGA', 'HIGH', 'LOW', 'DAILY', 'MONTHLY', 'QUARTERLY', 'ANNUAL', 'SHORT', 'LONG',
      'EMERGING', 'DEVELOPED', 'FRONTIER', 'BALANCED', 'GROWTH', 'VALUE', 'INCOME', 'DIVIDEND', 'QUALITY',
      'MOMENTUM', 'VOLATILITY', 'BOND', 'EQUITY', 'FIXED', 'VARIABLE', 'MULTI', 'SINGLE', 'DOUBLE', 'TRIPLE',
      'ESG', 'SRI', 'SUSTAINABLE', 'GREEN', 'CLEAN', 'TECHNOLOGY', 'TECH', 'HEALTHCARE', 'ENERGY', 'MATERIALS',
      'FINANCIALS', 'UTILITIES', 'INDUSTRIALS', 'CONSUMER', 'COMMUNICATION', 'REAL', 'ESTATE', 'REIT'
    ];

    data.forEach(etf => {
      if (!etf.fundName) return;
      
      const upperName = etf.fundName.toUpperCase();
      let provider = null;
      
      // First try: Check if any known provider appears in the fund name
      for (const knownProvider of knownProviders) {
        if (upperName.includes(knownProvider)) {
          provider = knownProvider;
          break;
        }
      }
      
      // Second try: Extract first meaningful word as potential provider
      if (!provider) {
        const words = etf.fundName.trim().split(/\s+/);
        for (const word of words) {
          const cleanWord = word.toUpperCase().replace(/[^A-Z]/g, ''); // Remove non-letters
          if (cleanWord.length >= 3 && !junkWords.includes(cleanWord)) {
            provider = cleanWord;
            break;
          }
        }
      }
      
      if (provider) {
        const current = providerStats.get(provider) || { count: 0, aum: 0 };
        providerStats.set(provider, {
          count: current.count + 1,
          aum: current.aum + (etf.aum || 0)
        });
      }
    });

    const topProvider = Array.from(providerStats.entries())
      .sort((a, b) => b[1].aum - a[1].aum)[0] || ['Unknown', { aum: 0 }];

    const esgETFs = data.filter(etf => etf.fundESG === 'YES');
    const esgAUM = esgETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);

    const irishETFs = data.filter(etf => etf.fundDomicile === 'Ireland');
    const irishAUM = irishETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);

    const equityETFs = data.filter(etf => etf.fundAssetClass === 'Equity');
    const equityAUM = equityETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);

    const bondETFs = data.filter(etf => etf.fundAssetClass === 'Fixed Income');
    const bondAUM = bondETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);

    // Other Assets: Multi-Asset, Cryptocurrency, Commodity, Alternatives
    const otherAssetClasses = ['Multi-Asset', 'Cryptocurrency', 'Commodity', 'Alternatives'];
    const otherETFs = data.filter(etf => otherAssetClasses.includes(etf.fundAssetClass));
    const otherAUM = otherETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);

    const lowCostETFs = data.filter(etf => (etf.fundTER || 0) <= 0.005);
    const ultraLowCostETFs = data.filter(etf => (etf.fundTER || 0) <= 0.002);

    return {
      // Core statistics calculation
      totalETFs: data.length,
      assetClasses: new Set(data.map(etf => etf.fundAssetClass)).size,
      domiciles: new Set(data.map(etf => etf.fundDomicile)).size,
      currencies: new Set(data.map(etf => etf.fundCurrency)).size,
      totalAUM,
      avgTER: totalAUM > 0 ? data.reduce((sum, etf) => sum + ((etf.fundTER || 0) * (etf.aum || 0)), 0) / totalAUM : 0,

      // 💎 PREMIUM INSIGHTS
      largestETF,
      topProvider,
      esgETFs: esgETFs.length,
      esgAUM,
      esgPenetration: totalAUM > 0 ? (esgAUM / totalAUM) * 100 : 0,
      irishETFs: irishETFs.length,
      irishAUM,
      irishMarketShare: totalAUM > 0 ? (irishAUM / totalAUM) * 100 : 0,
      equityETFs: equityETFs.length,
      equityAUM,
      equityPenetration: totalAUM > 0 ? (equityAUM / totalAUM) * 100 : 0,
      bondETFs: bondETFs.length,
      bondAUM,
      bondPenetration: totalAUM > 0 ? (bondAUM / totalAUM) * 100 : 0,
      otherAssetsETFs: otherETFs.length,
      otherAssetsAUM: otherAUM,
      otherAssetsPenetration: totalAUM > 0 ? (otherAUM / totalAUM) * 100 : 0,
      lowCostETFs: lowCostETFs.length,
      ultraLowCostETFs: ultraLowCostETFs.length,
      lowCostPenetration: data.length > 0 ? (lowCostETFs.length / data.length) * 100 : 0,
      providersCount: providerStats.size,
      topProviderMarketShare: totalAUM > 0 ? (topProvider[1].aum / totalAUM) * 100 : 0
    };
  };

  const overviewStats = getOverviewStats(etfData || []);

    /**
   * Get the latest ETF data date from settings
   * 
   * Extracts the LatestETFDataRepresented value from the settings array
   * for display in the coverage information section.
   * 
   * @returns {string} Formatted date string from settings or default fallback
   */
  const getLatestDataDate = () => {
    if (!settings || !Array.isArray(settings)) return 'Data date not available';
    
    const latestDataSetting = settings.find(setting => 
      setting.setting === 'LatestETFDataRepresented'
    );
    
    return latestDataSetting ? latestDataSetting.value : 'Data date not available';
  };

  /**
   * Reusable ETF Coverage Information Component
   * 
   * Displays key information about data coverage and freshness.
   * Used on both Screener and Overview tabs for consistency.
   * 
   * @returns {JSX.Element} Coverage information section
   */
  const renderETFCoverageInfo = () => (
    <div className="etf-coverage-section">
      <div className="coverage-content">
        <div className="coverage-item">
          <span className="coverage-label">Domicile Coverage:</span>
          <span className="coverage-value">Europe, <span style={{fontSize: '0.6em'}}>UAE</span></span>
        </div>
        <div className="coverage-item">
          <span className="coverage-label">Latest Data Represented 'as at' </span>
          <span>{getLatestDataDate()}</span>
        </div>
      </div>
    </div>
  );

  /**
   * Generate dynamic market insights with IFIA branding and analytics
   * 
   * Creates contextual business intelligence insights with branded color schemes
   * and metrics that matter to Irish Funds members. Includes special features
   * for fullscreen mode and responsive analytics.
   * 
   * @returns {Array} Array of insight objects with visual and analytical data
   * @returns {string} returns[].icon - FontAwesome icon class
   * @returns {string} returns[].title - Insight title
   * @returns {string} returns[].value - Primary metric value
   * @returns {string} returns[].subtitle - Additional context/breakdown
   * @returns {string} returns[].color - IFIA brand color
   * @returns {string} returns[].gradient - CSS gradient for visual appeal
   * @returns {boolean} returns[].pulse - Optional animation flag for fullscreen mode
   */
  const getDynamicInsights = () => {
    const data = etfData || [];
    const stats = getOverviewStats(data);
    if (data.length === 0) return [];

    const insights = [];

          // Total ETF share classes (Ireland only)
    insights.push({
      icon: 'fas fa-flag',
      title: `${stats.irishETFs.toLocaleString()}`,
      value: 'Total ETF share classes',
      color: '#169B62', // Irish Flag Green
      textColor: '#ffffff' // White text on green
    });

          // Monthly flows data
    const getLatestMonthlyFlow = () => {
      if (!graphData?.auMByDomicileTrend || !graphData.auMByDomicileTrend.length) {
        return { flow: 0, isNegative: false };
      }

              // Prefer real flows data over calculated flows
      const useRealFlowsData = graphData.flowsByDomicileTrend && graphData.flowsByDomicileTrend.length > 0;
      const flowsSource = useRealFlowsData ? graphData.flowsByDomicileTrend : graphData.auMByDomicileTrend;
      
      const irelandData = flowsSource.find(item => 
        item.name && item.name.toLowerCase() === 'ireland'
      );
      
      if (!irelandData || !irelandData.data || irelandData.data.length < 2) {
        return { flow: 0, isNegative: false };
      }

      // Group by month and get the two most recent months
      const monthlyData = new Map();
      irelandData.data.forEach(point => {
        const date = parseDate(point.dateTime);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData.set(monthKey, {
          date: date,
          value: point.value
        });
      });

      const sortedMonthly = Array.from(monthlyData.values())
        .sort((a, b) => a.date - b.date);

      if (useRealFlowsData) {
        // Calculate flows from cumulative AUM data
        // Calculate actual flow from difference between latest two months
        if (sortedMonthly.length < 2) {
          return { flow: 0, isNegative: false };
        }

        const latest = sortedMonthly[sortedMonthly.length - 1];
        const previous = sortedMonthly[sortedMonthly.length - 2];
        const cumulativeAUM_latest = latest.value / 1000000000; // Convert to billions
        const cumulativeAUM_previous = previous.value / 1000000000; // Convert to billions
        const actualFlow = cumulativeAUM_latest - cumulativeAUM_previous; // Monthly flow
        
        return {
          flow: Math.abs(actualFlow), // Convert to billions and get absolute value
          isNegative: actualFlow < 0,
          date: latest.date
        };
      } else {
        // 🧮 CALCULATED FLOWS: Calculate from AUM differences
        if (sortedMonthly.length < 2) {
          return { flow: 0, isNegative: false };
        }

        const latest = sortedMonthly[sortedMonthly.length - 1];
        const previous = sortedMonthly[sortedMonthly.length - 2];
        const flowValue = latest.value - previous.value;
        
        return {
          flow: Math.abs(flowValue / 1000000000), // Convert to billions and get absolute value
          isNegative: flowValue < 0,
          date: latest.date
        };
      }
    };

    const latestFlow = getLatestMonthlyFlow();
    const flowDisplayValue = latestFlow.isNegative 
      ? `(€${latestFlow.flow.toFixed(1)}B)`
      : `€${latestFlow.flow.toFixed(1)}B`;
    
    const flowColor = latestFlow.isNegative ? '#bd2130' : '#169B62'; // Red for negative, Green for positive
    const flowTextColor = '#ffffff'; // White text on both colors

    insights.push({
      icon: 'fas fa-water',
      title: flowDisplayValue,
      value: 'Monthly flows',
      color: flowColor,
      textColor: flowTextColor
    });

          // Asset class breakdown (Ireland only)
    const getIrelandAssetBreakdown = () => {
      const irelandETFs = data.filter(etf => etf.fundDomicile === 'Ireland');
      const irelandTotalAUM = irelandETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);
      

      
      if (irelandTotalAUM === 0) {
        return { equity: 0, bonds: 0, multiAsset: 0 };
      }
      
      const irelandEquityAUM = irelandETFs
        .filter(etf => etf.fundAssetClass === 'Equity')
        .reduce((sum, etf) => sum + (etf.aum || 0), 0);
        
      const irelandBondAUM = irelandETFs
        .filter(etf => etf.fundAssetClass === 'Fixed Income')
        .reduce((sum, etf) => sum + (etf.aum || 0), 0);
        
      const irelandOtherAUM = irelandETFs
        .filter(etf => ['Multi-Asset', 'Cryptocurrency', 'Commodity', 'Alternatives'].includes(etf.fundAssetClass))
        .reduce((sum, etf) => sum + (etf.aum || 0), 0);
      

      
      const breakdown = {
        equity: (irelandEquityAUM / irelandTotalAUM) * 100,
        bonds: (irelandBondAUM / irelandTotalAUM) * 100,
        other: (irelandOtherAUM / irelandTotalAUM) * 100
      };
      

      
      return breakdown;
    };
    
    const irelandAssets = getIrelandAssetBreakdown();
    
    insights.push({
      icon: 'fas fa-chart-pie',
      title: `${irelandAssets.equity.toFixed(1)}% - Equity`,
      value: `${irelandAssets.bonds.toFixed(1)}% - Bonds`,
      subtitle: `${irelandAssets.other.toFixed(1)}% - Other`,
      subtitleTooltip: 'Other Assets includes: Multi-Asset, Crypto, Commodity, Alternative',
      color: '#169B62', // Irish Flag Green
      textColor: '#ffffff', // White text on green
      className: 'asset-breakdown' // Special class for equal sizing
    });

    // IRISH ETFs - Deduplicated count by domicile using ISIN
    const irishETFs = data.filter(etf => etf.fundDomicile === 'Ireland');
    const allETFs = data;
    
    // Get unique ETFs by ISIN (deduplicated)
    const uniqueIrishISINs = new Set(irishETFs.map(etf => etf.fundISIN).filter(isin => isin));
    const uniqueAllISINs = new Set(allETFs.map(etf => etf.fundISIN).filter(isin => isin));
    
    const irishETFCount = uniqueIrishISINs.size;  // Deduplicated count
    const totalETFCount = uniqueAllISINs.size;    // Deduplicated count
    const irishETFPercentage = totalETFCount > 0 ? ((irishETFCount / totalETFCount) * 100) : 0;
    




          // Total ETF value (Ireland market size)
    const irelandTotalAUM = irishETFs.reduce((sum, etf) => sum + (etf.aum || 0), 0);
    insights.push({
      icon: 'fas fa-euro-sign',
      title: `€${(irelandTotalAUM / 1000000000).toFixed(1)}B`,
      value: 'Total ETF Value',
      color: '#169B62', // Irish Flag Green
      textColor: '#ffffff' // White text on green
    });









    return insights;
  };

  /**
   * Generate pie chart data for AUM distribution by asset class
   * 
   * Processes the graphData prop to create Chart.js compatible data structure
   * for visualizing assets under management breakdown by asset class.
   * 
   * @returns {Object} Chart.js pie chart data object
   * @returns {Array} returns.labels - Asset class names
   * @returns {Array} returns.datasets - Chart dataset with values and styling
   */
  const getAUMByAssetClassPieData = () => {
    const data = etfData || [];

    // Calculate AUM by asset class from actual ETF data
    const assetClassMap = new Map();
    data.forEach(etf => {
      const assetClass = etf.fundAssetClass || 'Unknown';
      const aum = etf.aum || 0;

      if (assetClassMap.has(assetClass)) {
        assetClassMap.set(assetClass, assetClassMap.get(assetClass) + aum);
      } else {
        assetClassMap.set(assetClass, aum);
      }
    });

    // Convert to array and sort by AUM descending
    const sortedData = Array.from(assetClassMap.entries())
      .map(([assetClass, totalAum]) => ({ assetClass, totalAum }))
      .filter(item => item.totalAum > 0)
      .sort((a, b) => b.totalAum - a.totalAum);

    const colors = [
      '#22C55E', // Green for Equity (should be largest)
      '#3B82F6', // Blue for Fixed Income
      '#F59E0B', // Amber for Commodity
      '#8B5CF6', // Purple for Multi-Asset
      '#EF4444', // Red for Alternatives
      '#6B7280', // Gray for other
    ];

    return {
      labels: sortedData.map(item => item.assetClass),
      datasets: [{
        data: sortedData.map(item => item.totalAum),
        backgroundColor: colors.slice(0, sortedData.length),
        borderColor: colors.slice(0, sortedData.length).map(color => color + '80'),
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 10
      }]
    };
  };

  /**
   * Generate pie chart data for AUM distribution by asset class - Ireland only
   * Filters ETF data to show only Ireland-domiciled funds
   * @returns {Object} Chart.js pie chart data object with Ireland-specific data
   */
  const getAUMByAssetClassIrelandPieData = () => {
    const data = etfData || [];

    // Filter for Ireland-domiciled funds only
    const irelandFunds = data.filter(etf =>
      etf.fundDomicile && etf.fundDomicile.toLowerCase().includes('ireland')
    );

    // Calculate AUM by asset class for Ireland funds
    const assetClassMap = new Map();
    irelandFunds.forEach(etf => {
      const assetClass = etf.fundAssetClass || 'Unknown';
      const aum = etf.aum || 0;

      if (assetClassMap.has(assetClass)) {
        assetClassMap.set(assetClass, assetClassMap.get(assetClass) + aum);
      } else {
        assetClassMap.set(assetClass, aum);
      }
    });

    // Convert to array and sort by AUM descending
    const sortedData = Array.from(assetClassMap.entries())
      .map(([assetClass, totalAum]) => ({ assetClass, totalAum }))
      .filter(item => item.totalAum > 0)
      .sort((a, b) => b.totalAum - a.totalAum);

    // Ireland-specific color scheme matching the image
    const irelandColors = [
      '#22C55E', // Irish Green (for Equity - largest slice)
      '#3B82F6', // Blue (for Fixed Income)  
      '#F59E0B', // Orange (for Commodity)
      '#8B5CF6', // Purple (for Multi Asset)
      '#EF4444', // Red (for Alternatives)
      '#6B7280', // Gray (for Cryptocurrency)
      '#10B981', // Emerald backup
      '#06B6D4'  // Cyan backup
    ];

    return {
      labels: sortedData.map(item => item.assetClass),
      datasets: [{
        data: sortedData.map(item => item.totalAum),
        backgroundColor: irelandColors.slice(0, sortedData.length),
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverOffset: 12,
        hoverBorderWidth: 4
      }]
    };
  };

  const getAUMByProviderPieData = () => {
    const data = etfData || [];

    // Extract provider from fund name and calculate AUM
    const providerMap = new Map();
    
    data.forEach(etf => {
      if (!etf.fundName || !etf.aum) return;
      
      // Simple provider extraction from fund name (first word, cleaned)
      const firstWord = etf.fundName.trim().split(/\s+/)[0];
      const provider = firstWord.toUpperCase().replace(/[^A-Z]/g, '');
      
      if (provider.length >= 2) { // Valid provider name
        const currentAUM = providerMap.get(provider) || 0;
        providerMap.set(provider, currentAUM + etf.aum);
      }
    });

    // Sort by AUM descending and take top 10
    const sortedProviders = Array.from(providerMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Calculate remaining AUM as "Others"
    const totalTopAUM = sortedProviders.reduce((sum, [, aum]) => sum + aum, 0);
    const totalAUM = Array.from(providerMap.values()).reduce((sum, aum) => sum + aum, 0);
    const othersAUM = totalAUM - totalTopAUM;

    // Create final data
    const finalData = sortedProviders.map(([provider, aum]) => ({ name: provider, value: aum }));
    if (othersAUM > 0) {
      finalData.push({ name: 'Others', value: othersAUM });
    }

    const colors = [
      '#4F46E5', '#7C3AED', '#EC4899', '#EF4444', '#F97316',
      '#EAB308', '#22C55E', '#06B6D4', '#3B82F6', '#8B5CF6',
      '#94A3B8' // Gray color for "Others"
    ];

    return {
      labels: finalData.map(item => item.name),
      datasets: [{
        data: finalData.map(item => item.value),
        backgroundColor: colors.slice(0, finalData.length),
        borderColor: colors.slice(0, finalData.length).map(color => color + '80'),
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 10
      }]
    };
  };

  // Flow data calculation functions
  
  // Calculate monthly flows from AUM time series data
  const getMonthlyFlowsData = (overrideCountryFilter = null) => {
    if (!graphData?.auMByDomicileTrend || !graphData.auMByDomicileTrend.length) {
      return { labels: [], datasets: [] };
    }
    

    
    return getMonthlyFlowsDataInternal(overrideCountryFilter);
  };
  
  const getMonthlyFlowsDataInternal = (overrideCountryFilter = null) => {

    // Use real flows data if available, otherwise fallback to AUM trend data
    const useRealFlowsData = graphData.flowsByDomicileTrend && graphData.flowsByDomicileTrend.length > 0;
    


    const flowsSource = useRealFlowsData ? graphData.flowsByDomicileTrend : graphData.auMByDomicileTrend;
    
    let processedData;
    const currentFilter = overrideCountryFilter || flowChartFilter;
    

    
    if (currentFilter === 'ireland') {
      // Process Ireland data only
      const irelandData = flowsSource.find(item => 
        item.name && item.name.toLowerCase() === 'ireland'
      );
      

      
      if (!irelandData || !irelandData.data || irelandData.data.length < 1) {
        return { labels: [], datasets: [] };
      }
      
      // Group by month and take last value of each month
      const monthlyData = new Map();
      irelandData.data.forEach(point => {
        const date = parseDate(point.dateTime);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData.set(monthKey, {
          date: date,
          value: point.value,
          monthKey: monthKey
        });
      });

      processedData = Array.from(monthlyData.values()).sort((a, b) => a.date - b.date);
      

      
    } else {
      // Process Europe data by aggregating CUMULATIVE AUM from all countries
      const monthlyAUMMap = new Map();
      let includedCountries = 0;
      
      flowsSource.forEach(countryData => {
        if (!countryData.data || countryData.data.length < 2) return;
        
        // Exclude UAE from Europe aggregate
        if (countryData.name && (
          countryData.name.toLowerCase().includes('uae') || 
          countryData.name.toLowerCase().includes('united arab emirates')
        )) {
          return;
        }
        
        includedCountries++;
        
        // Aggregate cumulative AUM values (not flows)
        countryData.data.forEach(point => {
          const date = new Date(point.dateTime);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          // Add this country's AUM to the Europe total for this month
          if (monthlyAUMMap.has(monthKey)) {
            monthlyAUMMap.get(monthKey).value += point.value;
          } else {
            monthlyAUMMap.set(monthKey, {
              date: date,
              value: point.value,
              monthKey: monthKey
            });
          }
        });
      });
      
      processedData = Array.from(monthlyAUMMap.values()).sort((a, b) => a.date - b.date);
    }

    if (processedData.length < 1) {
      return { labels: [], datasets: [] };
    }

    const flowData = [];
    
          if (useRealFlowsData) {
        // Calculate actual flows from cumulative AUM differences
      
      for (let i = 1; i < processedData.length; i++) {
        const currentMonth = processedData[i];
        const previousMonth = processedData[i - 1];
        const cumulativeAUM_current = currentMonth.value / 1000000000; // Convert to billions
        const cumulativeAUM_previous = previousMonth.value / 1000000000; // Convert to billions
        const actualFlow = cumulativeAUM_current - cumulativeAUM_previous; // Monthly flow
        

        
        flowData.push({
          date: currentMonth.date,
          flow: actualFlow,
          isNegative: actualFlow < 0,
          monthKey: currentMonth.monthKey
        });
        

      }
      
    } else {
      // 🧮 CALCULATED FLOWS: Calculate flows from AUM differences
      for (let i = 1; i < processedData.length; i++) {
        const currentMonth = processedData[i];
        const previousMonth = processedData[i - 1];
        const flow = currentMonth.value - previousMonth.value;
        
        flowData.push({
          monthKey: currentMonth.monthKey,
          date: currentMonth.date,
          flow: flow,
          flowInBillions: flow / 1000000000
        });
      }
    }

    // Take last 24 months for readability
    const recentFlows = flowData.slice(-24);
    
    const labels = recentFlows.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    });

    const flows = recentFlows.map(item => 
      useRealFlowsData ? item.flow : item.flowInBillions
    );
    

    
    // Color bars: Green for positive flows, Red for negative flows
    const backgroundColors = flows.map(flow => 
      flow >= 0 ? '#22C55E' : '#EF4444' // Green for inflows, Red for outflows
    );
    
    const borderColors = flows.map(flow => 
      flow >= 0 ? '#16A34A' : '#DC2626'
    );

    return {
      labels,
      datasets: [{
        label: 'Monthly Net Flows',
        data: flows,
        backgroundColor: monthlyFlowsChartType === 'area' ? 
          flows.map(flow => flow >= 0 ? 'rgba(34, 197, 94, 0.4)' : 'rgba(220, 38, 38, 0.4)') : // Area fill colors
          backgroundColors,
        borderColor: borderColors,
        borderWidth: monthlyFlowsChartType === 'area' ? 2 : monthlyFlowsChartType === 'line' ? 3 : 2,
        borderRadius: monthlyFlowsChartType === 'bar' ? 6 : 0,
        borderSkipped: monthlyFlowsChartType === 'bar' ? false : undefined,
        fill: monthlyFlowsChartType === 'area' ? 'origin' : false,
        tension: monthlyFlowsChartType === 'line' || monthlyFlowsChartType === 'area' ? 0.1 : 0,
        pointRadius: monthlyFlowsChartType === 'line' ? 4 : monthlyFlowsChartType === 'area' ? 2 : 0,
        pointHoverRadius: monthlyFlowsChartType === 'line' ? 6 : monthlyFlowsChartType === 'area' ? 4 : 0
      }]
    };
  }; // End of getMonthlyFlowsDataInternal

  // Generate multi-country flows data for line chart visualization
  const getMonthlyFlowsByDomicileData = (overrideViewMode = null, overrideStartYear = null) => {
    // TEMP: Revert to original data source to debug
    if (!graphData?.flowsByDomicileTrend || !graphData.flowsByDomicileTrend.length) {
      return { labels: [], datasets: [] };
    }
    


    // Use original data source temporarily
          const flowsSource = graphData.flowsByDomicileTrend;
      

      

      

    




    // Determine chart mode and year filter
    const CHART_MODE = overrideViewMode || flowsViewMode;
    const SELECTED_YEAR = overrideStartYear || selectedStartYear;
    


    // Get all available countries from data, excluding UAE
    const allCountries = flowsSource
      .filter(countryData => 
        countryData.name && 
        countryData.data && 
        countryData.data.length > 0 &&
        !countryData.name.toLowerCase().includes('uae') &&
        !countryData.name.toLowerCase().includes('united arab emirates')
      )
      .map(countryData => countryData.name.toLowerCase());

    const targetCountries = allCountries;
    
    // Generate colors for all countries
    const colorPalette = [
      '#22C55E', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', 
      '#F97316', '#84CC16', '#EC4899', '#6366F1', '#14B8A6', '#F59E0B',
      '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#84CC16', '#EC4899'
    ];
    
    const countryColors = { total: '#1F2937' };
    targetCountries.forEach((country, index) => {
      countryColors[country] = colorPalette[index % colorPalette.length];
    });

    // Process each country's data
    const countryDatasets = [];
    const monthlyDataMap = new Map();

    // First, collect all months from all countries to create consistent timeline
    flowsSource.forEach(countryData => {
      if (!countryData.data || countryData.data.length === 0) return;
      
      countryData.data.forEach(point => {
        const date = new Date(point.dateTime); // ISO format works fine with new Date()
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyDataMap.has(monthKey)) {
          monthlyDataMap.set(monthKey, { date, monthKey });
        }
      });
    });

    // Create sorted timeline filtered by selected start year
    const sortedMonths = Array.from(monthlyDataMap.values()).sort((a, b) => a.date - b.date);
    
    // Filter from selected start year onwards
    const startDate = new Date(`${SELECTED_YEAR}-01-01`);
    const filteredMonths = sortedMonths.filter(month => month.date >= startDate);
    
    const allLabels = filteredMonths.map(month => {
      return month.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    });
    
    // Create smart labels based on time period length
    const labels = allLabels.map((label, index) => {
      const month = filteredMonths[index];
      const year = month.date.getFullYear();
      const totalMonths = filteredMonths.length;
      
      // Always show first and last
      if (index === 0 || index === allLabels.length - 1) {
        return label;
      }
      
      // For short periods (12 months or less): show all months
      if (totalMonths <= 12) {
        return label;
      }
      // For medium periods (13-24 months): show every other month
      else if (totalMonths <= 24) {
        if (index % 2 === 0) {
          return label;
        }
      }
      // For longer periods (25+ months): use year-based logic
      else {
        // For 2022 onwards: show quarterly (every 3 months)
        if (year >= 2022) {
          if (index % 3 === 0) {
            return label;
          }
        }
        // For 2018-2021: show bi-annually (every 6 months)
        else if (year >= 2018) {
          if (index % 6 === 0) {
            return label;
          }
        }
        // For older years (pre-2018): show yearly (every 12 months)
        else {
          if (index % 12 === 0) {
            return label;
          }
        }
      }
      
      // Return empty string for hidden labels
      return '';
    });
    
    // Smart labels created with adaptive frequency for better readability



    // Track total flows across all countries for each month
    const totalFlowsMap = new Map();
    filteredMonths.forEach(month => {
      totalFlowsMap.set(month.monthKey, 0);
    });

    // Process each target country
    targetCountries.forEach(targetCountry => {
      const countryData = flowsSource.find(item => 
        item.name && item.name.toLowerCase() === targetCountry
      );

      if (!countryData || !countryData.data || countryData.data.length < 2) {
        return;
      }

      let chartData;

      if (CHART_MODE === 'AUM') {
        // Show AUM values (end-of-month snapshots)
        const monthlyLatestMap = new Map();
        countryData.data.forEach(point => {
          const date = new Date(point.dateTime);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          // Keep the latest (highest) value for each month
          if (!monthlyLatestMap.has(monthKey) || date > monthlyLatestMap.get(monthKey).date) {
            monthlyLatestMap.set(monthKey, {
              date,
              value: point.value / 1000000000, // Convert to billions
              monthKey
            });
          }
        });

        chartData = filteredMonths.map(month => {
          const monthData = monthlyLatestMap.get(month.monthKey);
          return monthData ? monthData.value : 0;
        });

      } else if (CHART_MODE === 'MONTHLY_FLOWS') {
        // Calculate monthly flows using proven monthly aggregation approach

        // Use same approach as working getMonthlyFlowsData function
        // Group by month and take last value of each month
        const monthlyData = new Map();
        countryData.data.forEach(point => {
          const date = new Date(point.dateTime);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyData.set(monthKey, {
            date: date,
            value: point.value,
            monthKey: monthKey
          });
        });

        const sortedCountryData = Array.from(monthlyData.values()).sort((a, b) => a.date - b.date);
        
        // Process monthly data for flow calculations
        
        // Calculate month-to-month flows for this country (same as working function)
        const countryFlows = new Map();
        for (let i = 1; i < sortedCountryData.length; i++) {
          const currentMonth = sortedCountryData[i];
          const previousMonth = sortedCountryData[i - 1];
          const countryFlow = (currentMonth.value - previousMonth.value) / 1000000000; // Flow for this country this month
          
          // Calculate monthly flow for this country
          
          countryFlows.set(currentMonth.monthKey, countryFlow);
        }
        
        // Map to filtered months
        chartData = filteredMonths.map(month => {
          return countryFlows.get(month.monthKey) || 0;
        });

      } else {
        // Cumulative net flows (calculate monthly flows first, then accumulate from start year)
        
        // TEST: Use RAW cumulative values directly from flowsByDomicileTrend
        const monthlyData = new Map();
        countryData.data.forEach(point => {
          const date = new Date(point.dateTime);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyData.set(monthKey, {
            date: date,
            value: point.value,
            monthKey: monthKey
          });
        });

        const sortedCountryData = Array.from(monthlyData.values()).sort((a, b) => a.date - b.date);
        
        // Show cumulative flows based on selected view mode
        if (cumulativeViewMode === 'since_inception') {
          // Show actual cumulative amounts (never zero)
          chartData = filteredMonths.map(month => {
            const monthData = sortedCountryData.find(point => point.monthKey === month.monthKey);
            if (monthData) {
              return monthData.value / 1000000000;
            }
            return 0;
          });
        } else {
          // Show cumulative flows from baseline (starts at 0 for selected year)
          let cumulativeFlow = 0;
          chartData = [];
          
          for (let i = 0; i < filteredMonths.length; i++) {
            const currentMonth = filteredMonths[i];
            const currentData = sortedCountryData.find(point => point.monthKey === currentMonth.monthKey);
            
            if (i === 0) {
              // First month of selected period = start at 0
              chartData.push(0);
            } else {
              // Calculate flow from previous month in FILTERED data
              const previousMonth = filteredMonths[i - 1];
              const previousData = sortedCountryData.find(point => point.monthKey === previousMonth.monthKey);
              
              if (currentData && previousData) {
                const monthlyFlow = (currentData.value - previousData.value) / 1000000000;
                cumulativeFlow += monthlyFlow;
                chartData.push(cumulativeFlow);
              } else {
                chartData.push(cumulativeFlow); // Keep previous if no data
              }
            }
          }
        }
      }

      // Add this country's data to the total
      chartData.forEach((value, index) => {
        const monthKey = filteredMonths[index].monthKey;
        totalFlowsMap.set(monthKey, totalFlowsMap.get(monthKey) + value);
        

      });

      // Create dataset for this country
      countryDatasets.push({
        label: targetCountry.charAt(0).toUpperCase() + targetCountry.slice(1),
        data: chartData,
        borderColor: countryColors[targetCountry],
        backgroundColor: countryColors[targetCountry] + '20',
        borderWidth: 2,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.1
      });
    });

    // Create Europe dataset (Total renamed to Europe, UAE already excluded)
    const totalFlowsData = filteredMonths.map(month => totalFlowsMap.get(month.monthKey));
    const europeDataset = {
      label: 'Europe',
      data: totalFlowsData,
      borderColor: countryColors.total,
      backgroundColor: countryColors.total + '20',
      borderWidth: 3,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.1
    };
    

    


    // Keep actual cumulative amounts (no normalization to zero)
    // Starting point will be the actual cumulative amount at the start date

    return {
      labels,
      datasets: [europeDataset, ...countryDatasets]
    };
  };

  // Calculate AUM trend from historical data source
  const getCumulativeFlowsData = () => {
    if (!graphData?.auMByDomicileTrend || !graphData.auMByDomicileTrend.length) {
      return { labels: [], datasets: [] };
    }

    // Always use AUM trend data source for AUM progression
    const flowsSource = graphData.auMByDomicileTrend;

    let processedData;
    
    if (flowChartFilter === 'ireland') {
      // Ireland data only
      const irelandData = flowsSource.find(item => 
        item.name && item.name.toLowerCase() === 'ireland'
      );
      
      if (!irelandData || !irelandData.data || irelandData.data.length < 1) {
        return { labels: [], datasets: [] };
      }
      
      const monthlyData = new Map();
      irelandData.data.forEach(point => {
        const date = new Date(point.dateTime);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData.set(monthKey, {
          date: date,
          value: point.value
        });
      });

      processedData = Array.from(monthlyData.values()).sort((a, b) => a.date - b.date);
      


    } else {
      // Europe - aggregate all countries (excluding UAE)
      const globalFlowData = new Map();
      

      
      flowsSource.forEach(countryData => {
        if (!countryData.data || countryData.data.length === 0) return;
        
        // 🚫 EXCLUDE UAE from Europe aggregate
        if (countryData.name && (
          countryData.name.toLowerCase().includes('uae') || 
          countryData.name.toLowerCase().includes('united arab emirates')
        )) {
          return;
        }
        
        countryData.data.forEach(point => {
          const date = new Date(point.dateTime);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          if (globalFlowData.has(monthKey)) {
            globalFlowData.get(monthKey).value += point.value;
          } else {
            globalFlowData.set(monthKey, {
              date: date,
              value: point.value
            });
          }
        });
      });

      processedData = Array.from(globalFlowData.values()).sort((a, b) => a.date - b.date);
      

    }
      
    if (processedData.length < 1) {
      return { labels: [], datasets: [] };
    }

    // Show actual AUM values over time 
    const cumulativeData = [];
    
    // Use actual AUM values from the historical trend data
    for (let i = 0; i < processedData.length; i++) {
      cumulativeData.push({
        date: processedData[i].date,
        cumulativeFlow: processedData[i].value / 1000000000 // Convert to billions
      });
    }

    // Show last 24 months on main chart for readability
    // Complete historical data available in expanded modal view
    const recentData = cumulativeData.slice(-24);
    

    
    const labels = recentData.map(item => 
      item.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    );
    
    const values = recentData.map(item => item.cumulativeFlow);
    
    return {
      labels,
      datasets: [{
        label: 'AUM Trend (€B)',
        data: values,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      }]
    };
  };

  // Calculate total AUM over time (full dataset for modal expansion)
  const getCumulativeFlowsDataFull = () => {
    if (!graphData?.auMByDomicileTrend || !graphData.auMByDomicileTrend.length) {
      return { labels: [], datasets: [] };
    }

    // Always use AUM trend data source for AUM progression
    const flowsSource = graphData.auMByDomicileTrend;

    let processedData;
    
    if (flowChartFilter === 'ireland') {
      // Ireland data only
      const irelandData = flowsSource.find(item => 
        item.name && item.name.toLowerCase() === 'ireland'
      );
      
      if (!irelandData || !irelandData.data || irelandData.data.length < 1) {
        return { labels: [], datasets: [] };
      }
      
      const monthlyData = new Map();
      irelandData.data.forEach(point => {
        const date = new Date(point.dateTime);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData.set(monthKey, {
          date: date,
          value: point.value
        });
      });

      processedData = Array.from(monthlyData.values()).sort((a, b) => a.date - b.date);
      
    } else {
      // Europe - aggregate all countries (excluding UAE)
      const globalFlowData = new Map();
      
      flowsSource.forEach(countryData => {
        if (!countryData.data || countryData.data.length === 0) return;
        
        // 🚫 EXCLUDE UAE from Europe aggregate
        if (countryData.name && (
          countryData.name.toLowerCase().includes('uae') || 
          countryData.name.toLowerCase().includes('united arab emirates')
        )) {
          return;
        }
        
        countryData.data.forEach(point => {
          const date = new Date(point.dateTime);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          if (globalFlowData.has(monthKey)) {
            globalFlowData.get(monthKey).value += point.value;
          } else {
            globalFlowData.set(monthKey, {
              date: date,
              value: point.value
            });
          }
        });
      });

      processedData = Array.from(globalFlowData.values()).sort((a, b) => a.date - b.date);
    }
      
    if (processedData.length < 1) {
      return { labels: [], datasets: [] };
    }

    // Show FULL historical data in modal (all available months)
    const cumulativeData = processedData.map(item => ({
      date: item.date,
      cumulativeFlow: item.value / 1000000000 // Convert to billions
    }));
    
    const labels = cumulativeData.map(item => 
      item.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    );
    
    const values = cumulativeData.map(item => item.cumulativeFlow);

    return {
      labels,
      datasets: [{
        label: 'AUM Trend - Complete History (€B)',
        data: values,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 1,
        pointHoverRadius: 6,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      }]
    };
  };

  // NOTE: Historical time series data not available in current dataset
  // These functions show current data only as historical data requires time series source
  
  const getAUMAssetClassBarData = () => {
    const data = etfData || [];
    
    // Calculate current AUM by asset class from real data
    const assetClassMap = new Map();
    data.forEach(etf => {
      const assetClass = etf.fundAssetClass || 'Unknown';
      const aum = etf.aum || 0;
      assetClassMap.set(assetClass, (assetClassMap.get(assetClass) || 0) + aum);
    });

    // Sort by AUM descending  
    const sortedData = Array.from(assetClassMap.entries())
      .sort((a, b) => b[1] - a[1]);

    const colors = [
      '#22C55E', // Green for Equity (largest)
      '#3B82F6', // Blue for Fixed Income  
      '#F59E0B', // Orange for Commodity
      '#8B5CF6', // Purple for Multi-Asset
      '#EF4444', // Red for Alternatives
      '#6B7280', // Gray for other
    ];
    
    return {
      labels: sortedData.map(([assetClass]) => assetClass),
      datasets: [{
        label: 'AUM (€B)',
        data: sortedData.map(([, aum]) => aum / 1000000000), // Convert to billions
        backgroundColor: colors.slice(0, sortedData.length),
        borderColor: colors.slice(0, sortedData.length),
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
      }]
    };
  };

  // Alias for backward compatibility with existing chart reference
  const getAUMAssetClassTimeSeriesData = getAUMAssetClassBarData;

  

  // Chart data preparation functions
  const getAssetClassChartData = () => {
    const data = etfData || [];
    const assetClassMap = new Map();

    // Count ETFs and calculate AUM by asset class
    data.forEach(etf => {
      const assetClass = etf.fundAssetClass || 'Unknown';
      const current = assetClassMap.get(assetClass) || { count: 0, aum: 0 };
      assetClassMap.set(assetClass, {
        count: current.count + 1,
        aum: current.aum + (etf.aum || 0)
      });
    });

    const labels = Array.from(assetClassMap.keys());
    const counts = Array.from(assetClassMap.values()).map(v => v.count);
    const aums = Array.from(assetClassMap.values()).map(v => v.aum);

    // Beautiful color palette 🎨
    const colors = [
      '#4F46E5', // Indigo
      '#7C3AED', // Purple  
      '#EC4899', // Pink
      '#EF4444', // Red
      '#F97316', // Orange
      '#EAB308', // Yellow
      '#22C55E', // Green
      '#06B6D4', // Cyan
      '#3B82F6', // Blue
      '#8B5CF6'  // Violet
    ];

    return {
      labels,
      datasets: [{
        data: counts,
        backgroundColor: colors.slice(0, labels.length),
        borderColor: colors.slice(0, labels.length).map(color => color + '40'),
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 10
      }]
    };
  };

  const getDomicileChartData = () => {
    const data = etfData || [];
    const domicileMap = new Map();

    // Calculate AUM by domicile
    data.forEach(etf => {
      const domicile = etf.fundDomicile || 'Unknown';
      const current = domicileMap.get(domicile) || { count: 0, aum: 0 };
      domicileMap.set(domicile, {
        count: current.count + 1,
        aum: current.aum + (etf.aum || 0)
      });
    });

    // Sort by AUM descending and take top 10
    const sortedEntries = Array.from(domicileMap.entries())
      .sort((a, b) => b[1].aum - a[1].aum)
      .slice(0, 10);

    const labels = sortedEntries.map(([domicile]) => domicile);
    const aums = sortedEntries.map(([, data]) => data.aum / 1000000000); // Convert to billions

    // Beautiful gradient-like colors 🌈
    const colors = [
      '#4F46E5', '#5B21B6', '#7C2D92', '#9333EA', '#A855F7',
      '#C084FC', '#DDD6FE', '#E879F9', '#F0ABFC', '#FBBF24'
    ];

    return {
      labels,
      datasets: [{
        label: 'AUM (€B)',
        data: aums,
        backgroundColor: colors.slice(0, labels.length),
        borderColor: colors.slice(0, labels.length).map(color => color + '80'),
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      }]
    };
  };

  // Additional chart data function 📈
  const getTERDistributionChartData = () => {
    const data = etfData || [];

    // Create TER ranges
    const ranges = [
      { label: '0-0.25%', min: 0, max: 0.0025, color: '#22C55E' },
      { label: '0.25-0.5%', min: 0.0025, max: 0.005, color: '#84CC16' },
      { label: '0.5-0.75%', min: 0.005, max: 0.0075, color: '#EAB308' },
      { label: '0.75-1%', min: 0.0075, max: 0.01, color: '#F97316' },
      { label: '1%+', min: 0.01, max: Infinity, color: '#EF4444' }
    ];

    const distribution = ranges.map(range => ({
      ...range,
      count: data.filter(etf => {
        const ter = etf.fundTER || 0;
        return ter >= range.min && ter < range.max;
      }).length
    }));

    return {
      labels: distribution.map(d => d.label),
      datasets: [{
        data: distribution.map(d => d.count),
        backgroundColor: distribution.map(d => d.color),
        borderColor: distribution.map(d => d.color + '80'),
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8
      }]
    };
  };

  const getESGDistributionChartData = () => {
    const data = etfData || [];

    const esgCategories = new Map();
    data.forEach(etf => {
      if (etf.fundESG === 'YES') {
        const category = etf.fundESGCategory || 'General ESG';
        esgCategories.set(category, (esgCategories.get(category) || 0) + 1);
      }
    });

    // Add non-ESG count
    const nonESGCount = data.filter(etf => etf.fundESG !== 'YES').length;
    if (nonESGCount > 0) {
      esgCategories.set('Non-ESG', nonESGCount);
    }

    const sortedEntries = Array.from(esgCategories.entries())
      .sort((a, b) => b[1] - a[1]);

    const esgColors = [
      '#22C55E', // Green for ESG
      '#10B981', // Emerald
      '#059669', // Green-600
      '#047857', // Green-700
      '#065F46', // Green-800
      '#16A34A', // Additional green
      '#15803D', // Additional green
      '#166534'  // Additional green
    ];

    // Assign colors specifically - RED for Non-ESG, greens for ESG categories
    const backgroundColors = sortedEntries.map(([category], index) => {
      if (category === 'Non-ESG') {
        return '#EF4444'; // RED for Non-ESG as requested
      } else {
        return esgColors[index % esgColors.length]; // Cycle through green shades for ESG
      }
    });

    return {
      labels: sortedEntries.map(([category]) => category),
      datasets: [{
        data: sortedEntries.map(([, count]) => count),
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color + '80'),
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8
      }]
    };
  };

  /**
   * Professional pie chart configuration with responsive design
   * 
   * Chart.js options optimized for financial data presentation with
   * professional styling, responsive behavior, and accessibility features.
   * Includes custom tooltips and data label formatting.
   */
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    resize: true,
    devicePixelRatio: window.devicePixelRatio || 1,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#4F46E5',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} ETFs (${percentage}%)`;
          }
        }
      },
      datalabels: {
        color: '#ffffff',
        font: {
          weight: 'bold',
          size: 11
        },
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return percentage > 5 ? `${percentage}%` : ''; // Only show labels for slices > 5%
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    resize: true,
    devicePixelRatio: window.devicePixelRatio || 1,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#4F46E5',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function (context) {
            return `AUM: €${context.parsed.y.toFixed(1)}B`;
          }
        }
      },
      datalabels: {
        display: false // Clean look without data labels on bars
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '€' + value + 'B';
          },
          font: {
            size: 11,
            weight: '500'
          },
          color: '#6B7280'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        }
      },
      x: {
        ticks: {
          font: {
            size: 11,
            weight: '500'
          },
          color: '#6B7280',
          maxRotation: 45
        },
        grid: {
          display: false
        }
      }
    }
  };

  // Line chart options for time series ⏰
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    resize: true,
    devicePixelRatio: window.devicePixelRatio || 1,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 11,
            weight: '500'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#4F46E5',
        borderWidth: 1,
        cornerRadius: 8,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: €${context.parsed.y.toFixed(1)}B`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        type: 'category',
        ticks: {
          font: {
            size: 10,
            weight: '500'
          },
          color: '#6B7280',
          maxRotation: 45
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '€' + value + 'B';
          },
          font: {
            size: 10,
            weight: '500'
          },
          color: '#6B7280'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 4
      }
    }
  };

  // Chart options for flow visualizations
  const flowBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    resize: true,
    devicePixelRatio: window.devicePixelRatio || 1,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#4F46E5',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            const formattedValue = value >= 0 
              ? `€${Math.abs(value).toFixed(1)}B` 
              : `(€${Math.abs(value).toFixed(1)}B)`;
            const flowType = value >= 0 ? 'Net Inflows' : 'Net Outflows';
            return `${flowType}: ${formattedValue}`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#ffffff',
        font: {
          weight: 'bold',
          size: 14
        },
        rotation: 90,
        formatter: (value) => {
          if (Math.abs(value) < 0.5) return ''; // Hide small values for clarity
          return value >= 0 
            ? `€${Math.abs(value).toFixed(1)}B` 
            : `(€${Math.abs(value).toFixed(1)}B)`;
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value >= 0 
              ? `€${Math.abs(value)}B` 
              : `(€${Math.abs(value)}B)`;
          },
          font: {
            size: 11,
            weight: '500'
          },
          color: '#6B7280'
        },
        grid: {
          color: function(context) {
            if (context.tick.value === 0) {
              return '#374151'; // Darker line at zero
            }
            return 'rgba(0, 0, 0, 0.1)';
          },
          drawBorder: false,
          lineWidth: function(context) {
            if (context.tick.value === 0) {
              return 2; // Thicker line at zero
            }
            return 1;
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 12,
            weight: '500'
          },
          color: '#6B7280',
          maxRotation: 90,
          minRotation: 90,
          autoSkip: false,
          maxTicksLimit: false,
          includeBounds: true,
          callback: function(value, index, ticks) {
            const label = this.getLabelForValue(value);
            
            // Always show first and last labels
            if (index === 0 || index === ticks.length - 1) {
              return label;
            }
            
            // Show every 2nd label for clean spacing
            if (index % 2 === 0) {
              return label;
            }
            
            return '';
          }
        },
        grid: {
          display: false
        }
      }
    }
  };

  // Line chart options for monthly flows
  const monthlyFlowsLineChartOptions = {
    ...flowBarChartOptions,
    scales: {
      ...flowBarChartOptions.scales  // Inherit the fixed x-axis from bar chart
    },
    plugins: {
      ...flowBarChartOptions.plugins,
      datalabels: {
        display: true,
        color: function(context) {
          if (!context || !context.parsed || typeof context.parsed.y === 'undefined') {
            return '#374151';
          }
          return context.parsed.y >= 0 ? '#16A34A' : '#DC2626';
        },
        font: {
          weight: 'bold',
          size: 11
        },
        formatter: (value) => {
          if (typeof value === 'undefined' || value === null) return '';
          if (Math.abs(value) < 0.5) return '';
          return value >= 0 
            ? `€${Math.abs(value).toFixed(1)}B` 
            : `(€${Math.abs(value).toFixed(1)}B)`;
        }
      }
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6
      },
      line: {
        tension: 0.1
      }
    }
  };

  // Area chart options for monthly flows
  const monthlyFlowsAreaChartOptions = {
    ...monthlyFlowsLineChartOptions,
    plugins: {
      ...monthlyFlowsLineChartOptions.plugins,
      filler: {
        propagate: false
      },
      datalabels: {
        ...monthlyFlowsLineChartOptions.plugins.datalabels,
        color: function(context) {
          if (!context || !context.parsed || typeof context.parsed.y === 'undefined') {
            return '#374151';
          }
          return context.parsed.y >= 0 ? '#16A34A' : '#DC2626';
        }
      }
    },
          elements: {
        ...monthlyFlowsLineChartOptions.elements,
        line: {
          tension: 0.3,
          borderWidth: 2
        },
        point: {
          radius: 3,
          hoverRadius: 5
        }
      }
  };

  const flowLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    resize: true,
    devicePixelRatio: window.devicePixelRatio || 1,
    onClick: (event, elements, chart) => {
      // Only open modal if clicking on chart area, not legend
      // Legend clicks should toggle lines on/off (default Chart.js behavior)
      if (elements.length > 0 || (event.type === 'click' && event.target && event.target.tagName === 'CANVAS')) {
        openChartModal('line', `Flows by Domicile as at ${getLatestDataDate()}`, getMonthlyFlowsByDomicileData(), flowLineChartOptions);
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#3B82F6',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            const formattedValue = value >= 0 
              ? `€${Math.abs(value).toFixed(1)}B` 
              : `(€${Math.abs(value).toFixed(1)}B)`;
            return `Historical AUM: ${formattedValue}`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        type: 'category',
        ticks: {
          font: {
            size: 10,
            weight: '500'
          },
          color: '#6B7280',
          maxRotation: 45,
          autoSkip: false
        },
        grid: {
          display: false
        }
      },
      y: {
        ticks: {
          callback: function (value) {
            return value >= 0 
              ? `€${Math.abs(value)}B` 
              : `(€${Math.abs(value)}B)`;
          },
          font: {
            size: 10,
            weight: '500'
          },
          color: '#6B7280'
        },
        grid: {
          color: function(context) {
            if (context.tick.value === 0) {
              return '#374151'; // Darker line at zero
            }
            return 'rgba(0, 0, 0, 0.1)';
          },
          drawBorder: false,
          lineWidth: function(context) {
            if (context.tick.value === 0) {
              return 2; // Thicker line at zero
            }
            return 1;
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 6
      }
    }
  };

  // Get applied filters for tag display
  const getAppliedFilters = () => {
    const appliedFilters = [];

    if (searchTerm) {
      appliedFilters.push({
        type: 'search',
        label: 'Search',
        value: searchTerm,
        onRemove: () => setSearchTerm('')
      });
    }

    if (selectedAssetClass !== 'all') {
      appliedFilters.push({
        type: 'assetClass',
        label: 'Asset Class',
        value: selectedAssetClass,
        onRemove: () => setSelectedAssetClass('all')
      });
    }

    if (selectedDomicile !== 'all') {
      appliedFilters.push({
        type: 'domicile',
        label: 'Domicile',
        value: selectedDomicile,
        onRemove: () => setSelectedDomicile('all')
      });
    }

    if (selectedCurrency !== 'all') {
      appliedFilters.push({
        type: 'currency',
        label: 'Currency',
        value: selectedCurrency,
        onRemove: () => setSelectedCurrency('all')
      });
    }

    if (selectedESG !== 'all') {
      appliedFilters.push({
        type: 'esg',
        label: 'ESG',
        value: selectedESG,
        onRemove: () => setSelectedESG('all')
      });
    }

    if (selectedESGCategory !== 'all') {
      appliedFilters.push({
        type: 'esgCategory',
        label: 'ESG Category',
        value: selectedESGCategory,
        onRemove: () => setSelectedESGCategory('all')
      });
    }

    if (selectedSFDR !== 'all') {
      appliedFilters.push({
        type: 'sfdr',
        label: 'SFDR',
        value: formatSFDR(selectedSFDR),
        onRemove: () => setSelectedSFDR('all')
      });
    }

    return appliedFilters;
  };

  // Generate contextual stats description
  const getStatsDescription = () => {
    const appliedFilters = getAppliedFilters();
    const totalDataItems = etfData?.length || 0;
    
    // Calculate total AUM for filtered data
    const totalAUM = filteredData.reduce((sum, etf) => {
      const aumValue = parseFloat(etf.aum?.toString().replace(/[€,$MB\s]/g, '') || '0');
      return sum + (isNaN(aumValue) ? 0 : aumValue);
    }, 0);
    
    const formattedAUM = formatAUM(totalAUM);

    if (appliedFilters.length === 0) {
      return `${totalItems.toLocaleString()} of ${totalDataItems.toLocaleString()} ETFs shown • Total AUM: ${formattedAUM}`;
    }

    // Build contextual description based on filters
    const filterParts = [];

    if (selectedDomicile !== 'all') filterParts.push(`${selectedDomicile}-based`);
    if (selectedAssetClass !== 'all') filterParts.push(selectedAssetClass);
    if (selectedCurrency !== 'all') filterParts.push(`${selectedCurrency} currency`);
    if (selectedESG !== 'all') filterParts.push(`ESG ${selectedESG}`);
    if (selectedESGCategory !== 'all') filterParts.push(`${selectedESGCategory}`);
    if (selectedSFDR !== 'all') filterParts.push(`${formatSFDR(selectedSFDR)}`);

    const description = filterParts.length > 0
      ? `${totalItems.toLocaleString()} ${filterParts.join(' ')} ETFs • Total AUM: ${formattedAUM}`
      : `${totalItems.toLocaleString()} matching ETFs • Total AUM: ${formattedAUM}`;

    return description;
  };

  // Chart options for AUM Trend charts
  const aumTrendChartOptions = {
    ...flowLineChartOptions,
    onClick: undefined, // Remove inherited onClick from flowLineChartOptions
    scales: {
      ...flowLineChartOptions.scales,
      x: {
        ...flowLineChartOptions.scales.x,
        ticks: {
          ...flowLineChartOptions.scales.x.ticks,
          autoSkip: true,
          maxTicksLimit: 12,
          callback: function(value, index, ticks) {
            const label = this.getLabelForValue(value);
            
            // Always show first and last labels
            if (index === 0 || index === ticks.length - 1) {
              return label;
            }
            
            // Show every 2nd month for clean spacing
            if (index % 2 === 0) {
              return label;
            }
            
            return '';
          }
        }
      }
    }
  };

  // Chart options for modal views
  const modalLineChartOptions = {
    ...flowLineChartOptions,
    scales: {
      ...flowLineChartOptions.scales,
      x: {
        ...flowLineChartOptions.scales.x,
        ticks: {
          ...flowLineChartOptions.scales.x.ticks,
          autoSkip: true,
          autoSkipPadding: 30,
          maxTicksLimit: 25,
          maxRotation: 45,
          minRotation: 45,
          callback: function(value, index, ticks) {
            const label = this.getLabelForValue(value);
            
            // Always show first and last labels
            if (index === 0 || index === ticks.length - 1) {
              return label;
            }
            
            // Show every 4th tick for spacing
            if (index % 4 === 0) {
              return label;
            }
            
            return '';
          }
        }
      }
    }
  };

  // Chart modal handlers
  const openChartModal = (chartType, title, data, options) => {
    if (apiHelper._cachedUserEmail) {
      apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'ETFChartModal', title || 'Chart', 'Chart');
    }
    setChartModal({
      isOpen: true,
      chartType,
      title,
      data,
      options: {
        ...options,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          ...options.plugins,
          legend: {
            ...options.plugins?.legend,
            labels: {
              ...options.plugins?.legend?.labels,
              font: { size: 14 },
              padding: 20
            }
          }
        }
      }
    });
  };

  const closeChartModal = () => {
    setChartModal({ isOpen: false, chartType: null, title: '', data: null, options: null });
  };

      // Fullscreen API management 
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );

      if (isFullScreen && !isCurrentlyFullscreen) {
        // User exited fullscreen via ESC or other means
        setIsFullScreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [isFullScreen]);

  /**
   * Toggle fullscreen mode using native browser Fullscreen API
   * 
   * Integrates with browser's native fullscreen capabilities, providing
   * enhanced viewing experience for data analysis. Handles cross-browser
   * compatibility and state synchronization.
   * 
   * @callback toggleFullScreen
   * @returns {void}
   */
  const toggleFullScreen = useCallback(() => {
    if (!isFullScreen) {
      // Enter fullscreen - use browser's native fullscreen API
      const targetElement = document.documentElement; // Use entire page for maximum impact!
      if (targetElement.requestFullscreen) {
        targetElement.requestFullscreen();
      } else if (targetElement.webkitRequestFullscreen) {
        targetElement.webkitRequestFullscreen();
      } else if (targetElement.msRequestFullscreen) {
        targetElement.msRequestFullscreen();
      }
      setIsFullScreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullScreen(false);
    }
  }, [isFullScreen]);

  // 🎯 KEYBOARD HANDLING FOR FULLSCREEN! (ESC + F11)
  useEffect(() => {
    const handleKeyboard = (e) => {
      if (e.key === 'Escape' && isFullScreen) {
        // Exit fullscreen when ESC is pressed
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        setIsFullScreen(false);
      } else if (e.key === 'F11') {
        // Sync our state with F11 native fullscreen
        e.preventDefault();
        toggleFullScreen();
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [isFullScreen, toggleFullScreen]);

  if (loading) {
    return <LoadingSpinner text="Loading ETF Book" />;
  }

  return (
    <div ref={modalRef} className={`etf-browser-container ${isFullScreen ? 'fullscreen-mode' : ''}`}>
      {/* 🎯 BRAND ICONS FOR FULLSCREEN MODE */}
      {isFullScreen && (
        <div className="fullscreen-brand-icons">
          <img src="/assets/img/irish-logo.png" alt="Irish Funds" className="brand-icon irish-funds-icon" />
          <img src="/assets/img/etfbook.png" alt="ETF Book" className="brand-icon etf-book-icon" />
        </div>
      )}

      {/* Tab Navigation */}
      <div className="etf-tabs-section">
        <div className="container">
          <div className="etf-tabs-nav">
            <button
              className={`tab-btn ${activeTab === 'screener' ? 'active' : ''}`}
              onClick={() => setActiveTab('screener')}
            >
              <i className="fas fa-filter"></i>
              Screener
            </button>
            <button
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <i className="fas fa-chart-pie"></i>
              Overview
            </button>

            {/*  FULLSCREEN & DAIRE CONTROLS! */}
            <div className="fullscreen-controls">
              {process.env.REACT_APP_SHOW_ETF_DAIRE_INTELLIGENCE === 'true' && (
                <button
                  className="view-btn action-btn daire-btn"
                  onClick={handleCallDaire}
                  title="Ask Daire AI about this data"
                >
                  <img src="/assets/img/daire/daire-logo-icon.png" alt="Daire AI" className="daire-icon" />
                </button>
              )}
              <button
                className={`etf-fullscreen-btn-minimal ${isFullScreen ? 'active' : ''}`}
                onClick={toggleFullScreen}
                title={isFullScreen ? 'Exit fullscreen (ESC or F11)' : 'Enter fullscreen mode (F11 also works)'}
              >
                <i className={`fas ${isFullScreen ? 'fa-compress' : 'fa-expand'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'screener' ? (
        /* Screener Tab */
        <>
          {/* Search and Filter Controls - MediaCentre Style */}
          <div className="etf-filters-section">
            <div className="etf-filters-bar">
              {/* Top Row: Search, Asset Class Buttons, and View Controls */}
              <div className="search-and-controls-row">
                <div className="etf-search-box">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Search ETFs by name or ISIN..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Inline Asset Class Filter Buttons */}
                <div className="asset-class-filter-group inline">
                  <button
                    className={`asset-class-btn small ${selectedAssetClass === 'all' ? 'active' : ''}`}
                    onClick={() => handleSmartFilterChange('assetClass', 'all')}
                    title="All Asset Classes"
                  >
                    <i className="fas fa-th-large"></i>
                    All
                  </button>
                  <button
                    className={`asset-class-btn small ${selectedAssetClass === 'Equity' ? 'active' : ''}`}
                    onClick={() => handleSmartFilterChange('assetClass', 'Equity')}
                    title="Equity ETFs"
                  >
                    <i className="fas fa-chart-line"></i>
                    Equity
                  </button>
                  <button
                    className={`asset-class-btn small ${selectedAssetClass === 'Fixed Income' ? 'active' : ''}`}
                    onClick={() => handleSmartFilterChange('assetClass', 'Fixed Income')}
                    title="Fixed Income ETFs"
                  >
                    <i className="fas fa-percentage"></i>
                    Bonds
                  </button>
                  <button
                    className={`asset-class-btn small ${selectedAssetClass === 'Multi-Asset' ? 'active' : ''}`}
                    onClick={() => handleSmartFilterChange('assetClass', 'Multi-Asset')}
                    title="Multi-Asset ETFs"
                  >
                    <i className="fas fa-layer-group"></i>
                    Multi
                  </button>
                </div>

                {!mobileMode && (
                  <div className="view-controls">
                    <button
                      className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                      onClick={() => setViewMode('table')}
                      title="Table View"
                    >
                      <i className="fas fa-list"></i>
                    </button>
                    <button
                      className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <i className="fas fa-th"></i>
                    </button>

                    {/* Action Buttons */}
                    <button
                      className={`view-btn action-btn  ${viewMode === 'grid' ? 'disabled' : ''}`}
                      onClick={viewMode === 'table' ? handleExportToExcel : undefined}
                      disabled={viewMode === 'grid'}
                      title={viewMode === 'table' ? "Export current view to Excel" : "Export only available in Table view"}
                    >
                      <i className="fas fa-file-excel"></i>
                    </button>
                    <button
                      className={`view-btn action-btn print-btn ${viewMode === 'grid' ? 'disabled' : ''}`}
                      onClick={viewMode === 'table' ? handleRealPrint : undefined}
                      disabled={viewMode === 'grid'}
                      title={viewMode === 'table' ? "Print current ETF view" : "Print only available in Table view"}
                    >
                      <i className="fas fa-print"></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Bottom Row: Essential Filter Controls */}
              <div className="filter-controls">
                <div className="filter-wrapper">
                  <select
                    value={selectedDomicile}
                    onChange={(e) => {
                      setSelectedDomicile(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filtering
                    }}
                    className="filter-select"
                  >
                    <option value="all">All Domiciles</option>
                    {filterOptions.domiciles.map(domicile => (
                      <option key={domicile} value={domicile}>{domicile}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-wrapper">
                  <select
                    value={selectedCurrency}
                    onChange={(e) => {
                      setSelectedCurrency(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filtering
                    }}
                    className="filter-select"
                  >
                    <option value="all">All Currencies</option>
                    {filterOptions.currencies.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-wrapper">
                  <select
                    value={selectedESG}
                    onChange={(e) => {
                      setSelectedESG(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filtering
                    }}
                    className="filter-select"
                  >
                    <option value="all">All ESG</option>
                    {filterOptions.esgOptions.map(esg => (
                      <option key={esg} value={esg}>{esg}</option>
                    ))}
                  </select>
                  <button
                    className="filter-info-btn"
                    onMouseEnter={() => setActiveTooltip('esg')}
                    onMouseLeave={() => setActiveTooltip(null)}
                    aria-label="ESG Information"
                  >
                    <i className="fas fa-info-circle"></i>
                    {activeTooltip === 'esg' && (
                      <div className="tooltip-popup">
                        <strong>ESG:</strong> Environmental, Social, and Governance factors. Indicates whether the fund considers ESG criteria in its investment process.
                      </div>
                    )}
                  </button>
                </div>

                <div className="filter-wrapper">
                  <select
                    value={selectedESGCategory}
                    onChange={(e) => {
                      setSelectedESGCategory(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filtering
                    }}
                    className="filter-select"
                  >
                    <option value="all">All ESG Categories</option>
                    {filterOptions.esgCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-wrapper">
                  <select
                    value={selectedSFDR}
                    onChange={(e) => {
                      setSelectedSFDR(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filtering
                    }}
                    className="filter-select"
                  >
                    <option value="all">All SFDR Articles</option>
                    {filterOptions.sfdrArticles.map(article => (
                      <option key={article} value={article}>{formatSFDR(article)}</option>
                    ))}
                  </select>
                  <button
                    className="filter-info-btn"
                    onMouseEnter={() => setActiveTooltip('sfdr')}
                    onMouseLeave={() => setActiveTooltip(null)}
                    aria-label="SFDR Information"
                  >
                    <i className="fas fa-info-circle"></i>
                    {activeTooltip === 'sfdr' && (
                      <div className="tooltip-popup">
                        <strong>SFDR:</strong> Sustainable Finance Disclosure Regulation. Article 6 (no sustainability focus), Article 8 (promotes ESG), Article 9 (sustainable investment objective).
                      </div>
                    )}
                  </button>
                </div>

                <div className="filter-wrapper">
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="filter-select page-size-select"
                  >
                    {getDynamicPageSizes().map(size => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters Button - Icon Only */}
                {hasActiveFilters && (
                  <button
                    className="view-btn action-btn view-all-btn"
                    onClick={handleResetFilters}
                    title="Clear all filters and view all ETFs"
                  >
                    <i className="fas fa-times-circle"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Applied Filters & Stats Panel */}
          <div className="etf-filters-status">
            <div className="applied-filters-section">
              {getAppliedFilters().length > 0 && (
                <div className="applied-filters">
                  <span className="applied-filters-label">Applied filters:</span>
                  <div className="filter-tags">
                    {getAppliedFilters().map((filter, index) => (
                      <div key={`${filter.type}-${index}`} className="filter-tag">
                        <span className="filter-tag-label">{filter.label}:</span>
                        <span className="filter-tag-value">{filter.value}</span>
                        <button
                          className="filter-tag-remove"
                          onClick={filter.onRemove}
                          title={`Remove ${filter.label} filter`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="etf-instant-stats-panel">
              <div className="stats-description">
                {getStatsDescription()}
              </div>
              {totalItems < (etfData?.length || 0) && (
                <div className="stats-breakdown">
                  <i className="fas fa-filter"></i>
                  Filtered from {(etfData?.length || 0).toLocaleString()} total
                </div>
              )}
            </div>
          </div>

          {/* ETF Coverage Information */}
          {renderETFCoverageInfo()}

          {/* ETF Content */}
          <div className="etf-content-section">
            {displayData.length === 0 && totalItems > 0 ? (
              <div className="no-results">
                <p>No ETFs match your current filters. Try adjusting your search or filter criteria.</p>
              </div>
            ) : displayData.length === 0 ? (
              <div className="no-results">
                <p>No ETF data available.</p>
              </div>
            ) : viewMode === 'table' ? (
              /* Table View */
              <div className="etf-table-container">
                <div className="etf-table-wrapper">
                  <table className="etf-table">
                    <thead>
                      <tr>

                        <th className="sortable" onClick={() => handleSort('fundName')} title="Fund Name">
                          <span>NAME</span>
                          {getSortIcon('fundName')}
                        </th>
                        <th className="sortable" onClick={() => handleSort('fundDomicile')} title="Fund Domicile">
                          <span>DOMICILE</span>
                          {getSortIcon('fundDomicile')}
                        </th>
                        <th className="sortable" onClick={() => handleSort('fundCurrency')} title="Fund Currency">
                          <span>CCY</span>
                          {getSortIcon('fundCurrency')}
                        </th>
                        <th className="sortable" onClick={() => handleSort('fundAssetClass')} title="Fund Asset Class">
                          <span>ASSET CLASS</span>
                          {getSortIcon('fundAssetClass')}
                        </th>
                        <th className="sortable" onClick={() => handleSort('fundAssetClassGeoFocus')} title="Fund Asset Class Geographic Focus">
                          <span>GEO FOCUS</span>
                          {getSortIcon('fundAssetClassGeoFocus')}
                        </th>
                        <th className="sortable" onClick={() => handleSort('aum')} title="Assets Under Management">
                          <span>AUM</span>
                          {getSortIcon('aum')}
                        </th>
                        <th className="sortable" onClick={() => handleSort('fundSFDR')} title="Fund SFDR Classification">
                          <span>SFDR</span>
                          {getSortIcon('fundSFDR')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayData.map((etf, index) => (
                        <tr key={etf.fundISIN} className="etf-table-row clickable-row" onClick={() => handleRowClick(etf)}>

                          <td className="fund-name">
                            <span
                              className="fund-name-tooltip"
                              title={`${etf.fundName}\nISIN: ${etf.fundISIN}\nTER: ${formatTER(etf.fundTER)}\nAUM: ${formatAUM(etf.aum)}`}
                            >
                              {etf.fundName}
                            </span>
                          </td>
                          <td className="fund-domicile">{etf.fundDomicile}</td>
                          <td className="fund-currency">{etf.fundCurrency}</td>
                          <td className="fund-asset-class">{etf.fundAssetClass}</td>
                          <td className="fund-geo-focus">{etf.fundAssetClassGeoFocus}</td>
                          <td className="fund-aum">{formatAUM(etf.aum)}</td>
                          <td className="fund-sfdr">{formatSFDR(etf.fundSFDR)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Grid View */
              <div className="etf-grid">
                {displayData.map((etf, index) => (
                  <div key={etf.fundISIN} className="etf-card" onClick={() => handleRowClick(etf)}>
                    <div className="etf-card-header">
                      <h4 className="etf-name">{etf.fundName}</h4>
                      <span className="etf-isin">{etf.fundISIN}</span>
                    </div>
                    <div className="etf-card-body">

                      <div className="etf-detail">
                        <strong>Domicile:</strong> {etf.fundDomicile}
                      </div>
                      <div className="etf-detail">
                        <strong>Asset Class:</strong> {etf.fundAssetClass}
                      </div>
                      <div className="etf-detail">
                        <strong>Geographic Focus:</strong> {etf.fundAssetClassGeoFocus}
                      </div>
                      <div className="etf-detail">
                        <strong>Currency:</strong> {etf.fundCurrency}
                      </div>
                      <div className="etf-detail">
                        <strong>TER:</strong> {formatTER(etf.fundTER)}
                      </div>
                      <div className="etf-detail">
                        <strong>AUM:</strong> {formatAUM(etf.aum)}
                      </div>
                      <div className="etf-detail">
                        <strong>SFDR:</strong> <span className="etf-badge sfdr-badge">{formatSFDR(etf.fundSFDR)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Results Count and Pagination */}
            <div className="etf-pagination">
              <div className="pagination-info">
                Showing {totalItems === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)} of {totalItems} ETFs{totalPages > 1 ? ` | Page ${currentPage} of ${totalPages}` : ''}
              </div>
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    title="First Page"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Previous Page"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    title="Next Page"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    title="Last Page"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Overview Tab */
        <div className="etf-overview-section">
          <div className="container">
            {/*  TICKER TAPE */}
            <div className="market-ticker">
              <div className="ticker-content">
                {/* First set of ticker items */}
                <span className="ticker-item">
                  <i className="fas fa-globe"></i>
                  {overviewStats.totalETFs.toLocaleString()} ETFs Tracked
                </span>
                <span className="ticker-item">
                  <i className="fas fa-coins"></i>
                  {formatAUM(overviewStats.totalAUM)} Total AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-chart-line"></i>
                  Equity: {overviewStats.equityPenetration.toFixed(1)}% of AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-percentage"></i>
                  Bond: {overviewStats.bondPenetration.toFixed(1)}% of AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-cubes"></i>
                  Other Assets: {overviewStats.otherAssetsPenetration.toFixed(1)}% of AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-leaf"></i>
                  {overviewStats.esgETFs.toLocaleString()} ESG ETFs
                </span>
                <span className="ticker-item">
                  <i className="fas fa-calculator"></i>
                  Avg TER: {Math.round(overviewStats.avgTER * 10000)}bps
                </span>
                <span className="ticker-item">
                  <i className="fas fa-water"></i>
                  Monthly Flow Data Available
                </span>

                {/* Duplicate set for seamless scrolling */}
                <span className="ticker-item">
                  <i className="fas fa-globe"></i>
                  {overviewStats.totalETFs.toLocaleString()} ETFs Tracked
                </span>
                <span className="ticker-item">
                  <i className="fas fa-coins"></i>
                  {formatAUM(overviewStats.totalAUM)} Total AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-chart-line"></i>
                  Equity: {overviewStats.equityPenetration.toFixed(1)}% of AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-percentage"></i>
                  Bond: {overviewStats.bondPenetration.toFixed(1)}% of AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-cubes"></i>
                  Other Assets: {overviewStats.otherAssetsPenetration.toFixed(1)}% of AUM
                </span>
                <span className="ticker-item">
                  <i className="fas fa-leaf"></i>
                  {overviewStats.esgETFs.toLocaleString()} ESG ETFs
                </span>
                <span className="ticker-item">
                  <i className="fas fa-calculator"></i>
                  Avg TER: {Math.round(overviewStats.avgTER * 10000)}bps
                </span>
                <span className="ticker-item">
                  <i className="fas fa-water"></i>
                  Monthly Flow Data Available
                </span>
              </div>
            </div>

            {/* Overview Stats Grid */}
            <div className="overview-stats-grid">
              <div className="stat-card">
                <div className="stat-value">{overviewStats.totalETFs.toLocaleString()}</div>
                <div className="stat-label">Total ETF
                  <span className="stat-label-subtitle">
                    Share Classes
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.assetClasses}</div>
                <div className="stat-label">Asset Classes</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.domiciles}</div>
                <div className="stat-label">Domiciles</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.currencies}</div>
                <div className="stat-label">Currencies</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">€{(overviewStats.totalAUM / 1000000000).toFixed(1)}B</div>
                <div className="stat-label">Total AUM</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{Math.round(overviewStats.avgTER * 10000)}bps</div>
                <div className="stat-label">Average TER
                  <span className="stat-label-subtitle">
                    Asset Weighted TER
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.esgETFs.toLocaleString()}</div>
                <div className="stat-label">ESG ETF
                  <span className="stat-label-subtitle">
                    Share Classes
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.equityPenetration.toFixed(1)}%</div>
                <div className="stat-label">Equity
                  <span className="stat-label-subtitle">
                    % of Total AUM
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.bondPenetration.toFixed(1)}%</div>
                <div className="stat-label">Bond
                  <span className="stat-label-subtitle">
                    % of Total AUM
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{overviewStats.otherAssetsPenetration.toFixed(1)}%</div>
                <div className="stat-label">Other Assets
                  <span className="stat-label-subtitle">
                    % of Total AUM<br/>
                    Multi-Asset, Crypto, Commodity, Alternative
                  </span>
                </div>
              </div>
            </div>

            {/* ETF Coverage Information */}
            {renderETFCoverageInfo()}

            {/* Dynamic Insights Panel */}
            <div className="insights-panel">
              <div className="insights-header">
                <h3><i className="fas fa-lightbulb"></i> Key Insights for Ireland</h3>
                <p>Smart analysis of current ETF landscape</p>
              </div>
              <div className="insights-grid">
                {getDynamicInsights().map((insight, index) => (
                  <div
                    key={index}
                    className={`insight-card ${insight.pulse ? 'turbo-mode' : ''} ${insight.className || ''}`}
                    style={{
                      backgroundColor: insight.color,
                      border: `2px solid ${insight.color}20`,
                      color: insight.textColor || '#ffffff'
                    }}
                  >

                    <div className="insight-content">
                      <div className="insight-title" style={{ color: insight.textColor || '#ffffff' }}>{insight.title}</div>
                      <div className="insight-value" style={{ color: insight.textColor || '#ffffff' }}>{insight.value}</div>
                      {insight.subtitle && (
                        <div 
                          className="insight-subtitle" 
                          style={{ color: insight.textColor || '#ffffff' }}
                          title={insight.subtitleTooltip || undefined}
                        >
                          {insight.subtitle}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Essential Charts Section 📈 */}
            <div className="time-series-charts-section">

              {/* Flow Charts */}
              <div className="chart-container" onClick={() => openChartModal(monthlyFlowsChartType, `Monthly Net Flows as at ${getLatestDataDate()}`, getMonthlyFlowsData(), monthlyFlowsChartType === 'bar' ? flowBarChartOptions : monthlyFlowsChartType === 'line' ? monthlyFlowsLineChartOptions : monthlyFlowsAreaChartOptions)}>
                <div className="chart-header">
                  <div className="chart-header-row" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <h4><i className={`fas fa-chart-${monthlyFlowsChartType === 'bar' ? 'bar' : monthlyFlowsChartType === 'line' ? 'line' : 'area'}`}></i> Monthly Net Flows</h4>
                    <div className="monthly-chart-controls" style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                      <div className="chart-type-selector">
                        <select 
                          value={monthlyFlowsChartType} 
                          onChange={(e) => setMonthlyFlowsChartType(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '12px',
                            backgroundColor: 'white',
                            color: '#333',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="bar">Bar Chart</option>
                          <option value="line">Line Chart</option>
                          <option value="area">Area Chart</option>
                        </select>
                      </div>
                      <div className="flow-filter-dropdown">
                        <select 
                          value={flowChartFilter} 
                          onChange={(e) => setFlowChartFilter(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '12px',
                            backgroundColor: 'white',
                            color: '#333',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="ireland">Ireland</option>
                          <option value="europe">Europe</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="chart-subtitle-row" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <div>
                        <p style={{ fontSize: '11px', color: '#6B7280', fontStyle: 'italic', margin: 0 }}>
                        Click to expand for detailed view
                      </p>
                    </div>
                    <div className="chart-date-info">
                      <span className="coverage-label">Data updated as at </span>
                      <span>{getLatestDataDate()}</span>
                    </div>
                  </div>
                </div>
                <div className="chart-content">
                  <div className="chart-wrapper">
                    {monthlyFlowsChartType === 'bar' && (
                      <Bar
                        ref={monthlyFlowsBarRef}
                        data={getMonthlyFlowsData()}
                        options={flowBarChartOptions}
                      />
                    )}
                    {monthlyFlowsChartType === 'line' && (
                      <Line
                        data={getMonthlyFlowsData()}
                        options={monthlyFlowsLineChartOptions}
                      />
                    )}
                    {monthlyFlowsChartType === 'area' && (
                      <Line
                        data={getMonthlyFlowsData()}
                        options={monthlyFlowsAreaChartOptions}
                      />
                    )}
                  </div>
                </div>
              </div>

                              <div className="chart-container" onClick={() => openChartModal('line', `AUM Trend - Detailed View - ${flowChartFilter === 'ireland' ? 'Ireland' : 'Europe'} as at ${getLatestDataDate()}`, getCumulativeFlowsDataFull(), aumTrendChartOptions)}>
                <div className="chart-header">
                  <div className="chart-header-row">
                    <h4><i className="fas fa-chart-line"></i> AUM Trend - {flowChartFilter === 'ireland' ? 'Ireland' : 'Europe'}</h4>
                    <div className="chart-date-info">
                      <span className="coverage-label">Data updated as at </span>
                      <span>{getLatestDataDate()}</span>
                    </div>
                  </div>
                  <p className="chart-subtitle">{flowChartFilter === 'ireland' ? 'Ireland' : 'Europe'} AUM progression from historical data source (€B)</p>
                  <p style={{ fontSize: '11px', color: '#6B7280', fontStyle: 'italic', marginTop: '2px' }}>
                  Click to expand for detailed view
                  </p>
                </div>
                <div className="chart-content">
                  <div className="chart-wrapper">
                    <Line
                      ref={cumulativeFlowsLineRef}
                      data={getCumulativeFlowsData()}
                      options={aumTrendChartOptions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* FULL-WIDTH MULTI-COUNTRY FLOWS ANALYSIS */}
            {/* <div className="time-series-charts-section">
              <div className="chart-container chart-container-full-width">
                <div className="chart-header">
                  <div className="chart-header-row" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <h4><i className="fas fa-chart-line"></i> Flows by Domicile</h4>
                                          <div className="flows-chart-controls" style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                      <div className="cumulative-view-dropdown">
                        <select 
                          value={cumulativeViewMode} 
                          onChange={(e) => setCumulativeViewMode(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '12px',
                            backgroundColor: 'white',
                            color: '#333',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="from_baseline">From Baseline</option>
                          <option value="since_inception">Since Inception</option>
                        </select>
                      </div>
                      <div className="year-slicer-dropdown">
                        <select 
                          value={selectedStartYear} 
                          onChange={(e) => setSelectedStartYear(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '12px',
                            backgroundColor: 'white',
                            color: '#333',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="2015">2015</option>
                          <option value="2016">2016</option>
                          <option value="2017">2017</option>
                          <option value="2018">2018</option>
                          <option value="2019">2019</option>
                          <option value="2020">2020</option>
                          <option value="2021">2021</option>
                          <option value="2022">2022</option>
                          <option value="2023">2023</option>
                          <option value="2024">2024</option>
                          <option value="2025">2025</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="chart-subtitle-row" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <p className="chart-subtitle" style={{margin: 0}}>
                                            {`Cumulative Net Flows by Domicile (EUR bn) - ${cumulativeViewMode === 'since_inception' ? 'Since Inception' : 'From Baseline'}`}
                    </p>
                    <div className="chart-date-info">
                      <span className="coverage-label">Data updated as at </span>
                      <span>{getLatestDataDate()}</span>
                    </div>
                  </div>
                </div>
                <div className="chart-content">
                  <div className="chart-wrapper">
                    <Line
                      data={getMonthlyFlowsByDomicileData()}
                      options={flowLineChartOptions}
                    />
                  </div>
                </div>
              </div>
            </div> */}

            <div className="overview-charts-section">
              {/* Essential Charts Only */}
              <div className="chart-container" onClick={() => openChartModal('bar', 'AUM by Domicile', getDomicileChartData(), barChartOptions)}>
                <div className="chart-header">
                  <div className="chart-header-row">
                    <h4><i className="fas fa-globe-europe"></i> AUM by Domicile</h4>
                    <div className="chart-date-info">
                      <span className="coverage-label">Data updated as at </span>
                      <span>{getLatestDataDate()}</span>
                    </div>
                  </div>
                  <p className="chart-subtitle">Assets under management by domicile (€B)</p>
                </div>
                <div className="chart-content">
                  <div className="chart-wrapper">
                    <Bar
                      ref={barChartRef}
                      data={getDomicileChartData()}
                      options={barChartOptions}
                    />
                  </div>
                </div>
              </div>

              {/* Bottom Left */}
              <div className="chart-container" onClick={() => openChartModal('pie', `TER Distribution as at ${getLatestDataDate()}`, getTERDistributionChartData(), pieChartOptions)}>
                <div className="chart-header">
                  <div className="chart-header-row">
                    <h4><i className="fas fa-percentage"></i> TER Distribution</h4>
                    <div className="chart-date-info">
                      <span className="coverage-label">Data updated as at </span>
                      <span>{getLatestDataDate()}</span>
                    </div>
                  </div>
                  <p className="chart-subtitle">Total expense ratio breakdown - unweighted</p>
                </div>
                <div className="chart-content">
                  <div className="chart-wrapper">
                    <Pie
                      ref={pieChart2Ref}
                      data={getTERDistributionChartData()}
                      options={pieChartOptions}
                    />
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      )}

      {/* ETF Details Modal */}
      {selectedETF && (
        <div className="etf-modal-overlay" onClick={closeETFModal}>
          <div className="etf-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="etf-modal-header">
              <h3 className="etf-modal-title">{selectedETF.fundName}</h3>
              <button className="etf-modal-close" onClick={closeETFModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="etf-modal-body">
              <div className="etf-detail-grid">
                <div className="etf-detail-item">
                  <span className="detail-label">ISIN:</span>
                  <span className="detail-value">
                    <code>{selectedETF.fundISIN}</code>
                  </span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">TER:</span>
                  <span className="detail-value ter-value">{formatTER(selectedETF.fundTER)}</span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">AUM:</span>
                  <span className="detail-value aum-value">{formatAUM(selectedETF.aum)}</span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">Fund Domicile:</span>
                  <span className="detail-value">{selectedETF.fundDomicile}</span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">Asset Class:</span>
                  <span className="detail-value">{selectedETF.fundAssetClass}</span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">Geographic Focus:</span>
                  <span className="detail-value">{selectedETF.fundAssetClassGeoFocus}</span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">Currency:</span>
                  <span className="detail-value">{selectedETF.fundCurrency}</span>
                </div>

                {selectedETF.aumAsOfDate && (
                  <div className="etf-detail-item">
                    <span className="detail-label">AUM As Of Date:</span>
                    <span className="detail-value">{formatDateIrish(selectedETF.aumAsOfDate)}</span>
                  </div>
                )}

                <div className="etf-detail-item">
                  <span className="detail-label">ESG:</span>
                  <span className="detail-value">
                    {selectedETF.fundESG === 'YES' ? (
                      <span className="etf-badge esg-badge">{selectedETF.fundESGCategory || 'Yes'}</span>
                    ) : (
                      'No'
                    )}
                  </span>
                </div>

                <div className="etf-detail-item">
                  <span className="detail-label">SFDR Article:</span>
                  <span className="detail-value">
                    <span className="etf-badge sfdr-badge">{formatSFDR(selectedETF.fundSFDR)}</span>
                  </span>
                </div>


              </div>
            </div>
          </div>
        </div>
      )}

     
      {chartModal.isOpen && (
        <div className="chart-modal-overlay" onClick={closeChartModal}>
          <div className="chart-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <div className="chart-header-row">
                <h3 className="chart-modal-title">
                  <i className={`fas fa-chart-${chartModal.chartType === 'pie' ? 'pie' : chartModal.chartType === 'bar' ? 'bar' : chartModal.chartType === 'area' ? 'area' : 'line'}`}></i>
                  {chartModal.title}
                </h3>
                
                {/* MONTHLY NET FLOWS CONTROLS - Show when modal displays Monthly Net Flows */}
                {chartModal.title && chartModal.title.includes('Monthly Net Flows') && (
                  <div className="modal-chart-controls" style={{display: 'flex', alignItems: 'center', marginLeft: '15px', gap: '10px'}}>
                    <div className="chart-type-toggle">
                      <select 
                        value={monthlyFlowsChartType} 
                        onChange={(e) => {
                          const newChartType = e.target.value;
                          setMonthlyFlowsChartType(newChartType);
                          
                          // Small delay to ensure state has updated
                          setTimeout(() => {
                            const newOptions = newChartType === 'bar' ? flowBarChartOptions : 
                                             newChartType === 'line' ? monthlyFlowsLineChartOptions : 
                                             monthlyFlowsAreaChartOptions;
                            const newData = getMonthlyFlowsData();
                            
                            setChartModal(prev => ({
                              ...prev,
                              chartType: newChartType,
                              data: newData,
                              options: newOptions
                            }));
                          }, 50);
                        }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          color: '#333',
                          cursor: 'pointer'
                        }}
                                              >
                          <option value="bar">Bar Chart</option>
                          <option value="line">Line Chart</option>
                          <option value="area">Area Chart</option>
                        </select>
                    </div>
                    <div className="country-filter-toggle">
                      <select 
                        value={flowChartFilter} 
                        onChange={(e) => {
                          const newCountryFilter = e.target.value;
                          setFlowChartFilter(newCountryFilter);
                          // Update modal with new country data using override parameter
                          setChartModal(prev => ({
                            ...prev,
                            data: getMonthlyFlowsData(newCountryFilter)
                          }));
                        }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          color: '#333',
                          cursor: 'pointer'
                        }}
                      >
                          <option value="ireland">Ireland</option>
                          <option value="europe">Europe</option>
                      </select>
                    </div>
                  </div>
                )}

                {chartModal.title && (chartModal.title.includes('TER Distribution') || chartModal.title.includes('ESG Distribution') || chartModal.title.includes('AUM by Asset Class') || chartModal.title.includes('AUM by Provider') || chartModal.title.includes('Asset Class Distribution')) && (
                  <div className="chart-date-info">
                    <span className="coverage-label">Data updated as at </span>
                    <span>{getLatestDataDate()}</span>
                  </div>
                )}
              </div>
              <button className="chart-modal-close" onClick={closeChartModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="chart-modal-body">
                <div className="chart-modal-wrapper">
                  {chartModal.chartType === 'pie' && (
                    <Pie
                      data={chartModal.data}
                      options={chartModal.options}
                    />
                  )}
                  {chartModal.chartType === 'bar' && (
                    <Bar
                      key={`modal-bar-${chartModal.chartType}`}
                      data={chartModal.data}
                      options={chartModal.options}
                    />
                  )}
                  {chartModal.chartType === 'line' && (
                    <Line
                      key={`modal-line-${flowChartFilter}`}
                      data={{
                        ...chartModal.data,
                        datasets: chartModal.data.datasets.map(dataset => ({
                          ...dataset,
                          fill: false,
                          backgroundColor: dataset.borderColor
                        }))
                      }}
                      options={chartModal.options}
                    />
                  )}
                  {chartModal.chartType === 'area' && (
                    <Line
                      key={`modal-area-${flowChartFilter}`}
                      data={{
                        ...chartModal.data,
                        datasets: chartModal.data.datasets.map(dataset => ({
                          ...dataset,
                          fill: 'origin',
                          backgroundColor: dataset.data.map(value => 
                            value >= 0 ? 'rgba(34, 197, 94, 0.4)' : 'rgba(220, 38, 38, 0.4)'
                          )
                        }))
                      }}
                      options={{
                        ...chartModal.options,
                        plugins: {
                          ...chartModal.options.plugins,
                          filler: {
                            propagate: false
                          },
                          datalabels: {
                            ...chartModal.options.plugins?.datalabels,
                            color: function(context) {
                              if (!context || !context.parsed || typeof context.parsed.y === 'undefined') {
                                return '#374151';
                              }
                              return context.parsed.y >= 0 ? '#16A34A' : '#DC2626';
                            }
                          }
                        }
                      }}
                    />
                  )}
                </div>
          
            </div>
          </div>
        </div>
      )}

      {/* Excel Export Confirmation Modal */}
      {exportModal && (
        <div className="export-modal-overlay" onClick={cancelExportToExcel}>
          <div className="export-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="export-modal-header">
              <div className="export-modal-icon">
                <i className="fas fa-file-excel"></i>
              </div>
              <h3 className="export-modal-title">Export to Excel</h3>
              <button className="export-modal-close" onClick={cancelExportToExcel}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="export-modal-body">
              <div className="export-disclaimer">
                <div className="disclaimer-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <div className="export-disclaimer-text">
                  <strong>Data Usage Notice</strong>
                  <p>Data provided for member insight only. Redistribution prohibited without consent.</p>
                </div>
              </div>

              <div className="export-details">
                <h4>Export Details</h4>
                <div className="export-summary">
                  <div className="export-stat">
                    <span className="stat-label">Items to Export:</span>
                    <span className="stat-value">{displayData.length} ETFs</span>
                  </div>
                  <div className="export-stat">
                    <span className="stat-label">Current View:</span>
                    <span className="stat-value">Page {currentPage} of {totalPages}</span>
                  </div>
                  <div className="export-stat">
                    <span className="stat-label">Format:</span>
                    <span className="stat-value">CSV (Excel compatible)</span>
                  </div>
                </div>
              </div>

              <div className="export-actions">
                <button className="export-btn-cancel" onClick={cancelExportToExcel}>
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
                <button className="export-btn-confirm" onClick={confirmExportToExcel}>
                  <i className="fas fa-download"></i>
                  I Agree - Export Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 ETF INTELLIGENCE MODAL 🤖✨ */}
      {daireModal && (
        <div className="daire-ai-modal-overlay" onClick={closeDaireModal}>
          <div className="daire-ai-modal-content" onClick={(e) => e.stopPropagation()}>
            <ETFDaireChat onNavigateHome={closeDaireModal} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ETFBookBrowser; 